import { withBusyCursor } from './busyCursor.js';
// src/pcSelection.js
import { getMap } from './mapInstance.js';
import Select from 'ol/interaction/Select.js';
import { click } from 'ol/events/condition.js';
import { Style, Stroke, Fill, Circle as CircleStyle } from 'ol/style';
import { Draw } from 'ol/interaction.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import { fromCircle, circular } from 'ol/geom/Polygon.js';
import { toLonLat } from 'ol/proj.js';
import { makeDraggable, setStatus, setCommand, highlightFeaturesFromResult, isViewInLayerVisibilityRange } from './mapUtils.js';
import * as config from './config.js';
import { secureFetch } from './securityUtil.js';
import { getSessionContext } from './sessionContext.js';
import { getSelectedCountryAbbreviation } from './country-language.js';
import { resetActiveCommandsOnly } from './resetApplication.js';
import { placeFloatingDialog, setActiveSidebarTool, setSidebarToolActive } from './dialogs.js';
import { registerDock, setDockVisible } from './panelDockManager.js';
import { startDrawAutoPan, stopDrawAutoPan } from './autoPan.js';
import { geometryToShape, sendPostMessage } from './postMessages.js';
import { applyLanguageTranslations, refreshBootstrapTooltip, translate } from './languageTranslator.js';
import { createStyleFromColor } from './colorUtils.js';
import { hasRight } from './rightsUtil.js';
import { hideLoadingBar, showLoadingBar } from './loadingIndicator.js';
import { attachRadiusDrawLabel, createRadiusDrawStyle } from './radiusDrawStyle.js';
import { updateToolAvailabilityTooltip } from './toolAvailabilityTooltip.js';

const PC_DRAW_AUTOPAN = {
  edgeThreshold: 72,
  panSpeedPx: 5
};

const WEB_MERCATOR_WORLD_EXTENT = [
  -20037508.342789244,
  -20037508.342789244,
  20037508.342789244,
  20037508.342789244
];


/** ================================
 * State
 * ================================ */
let pcSelectInteraction = null;
let activePCSelectionLevel = null;
let pcDrawInteraction = null;
let isActive = false;
let vecSource = null;
let activePCType = null; // "PC4" | "PC6"
let pcDrawSessionActive = false;
let pcEscapeHandlerBound = false;
let pcEscapeHandler = null;
const pcDrawSelectionsByType = new Map();
let areaSearchSummaryRows = [];
let pcSummaryPanel = null;
let pcSummaryMinimized = false;
let pcSummaryExplicitlyOpened = false;
let pcSummaryStoredGeometry = null;
let pcSummaryDockButton = null;
let pcSummaryOpacity = 1;
let pcDrawLoadInFlight = false;
let pcSelectionDeleteInFlight = false;
let pcRemoveSelectionActive = false;
let pcRowDeleteInFlight = false;
let pcSummaryDeferredOpenToken = 0;
let pcSummaryPage = 1;
let pcSummaryActiveTab = 'details';
let pcSummarySort = { key: 'postcode', direction: 'asc' };
let pcSummaryFilterDialogKey = null;
const pcSummaryFilters = {
  postcode: '',
  place: '',
  addressCount: '',
  brutoVsp: '',
  nettoVsp: '',
  aantalWeekblad: '',
  brutoWeekblad: '',
  mailboxYesPub: '',
  mailboxNoPub: '',
  noPubPercentage: '',
  bdBruto: '',
  bdNetto: ''
};
const NETHERLANDS_PC_SUMMARY_COLUMNS = Object.freeze([
  { key: 'addressCount', label: 'Addresses', group: 'Overheid', slot: 'bruto' },
  { key: 'brutoVsp', label: 'Bruto VSP', group: 'PostNL', slot: 'bruto' },
  { key: 'nettoVsp', label: 'Netto VSP', group: 'PostNL', slot: 'netto' },
  { key: 'brutoWeekblad', label: 'Bruto Weekblad', group: 'Kranten', slot: 'bruto' },
  { key: 'aantalWeekblad', label: 'Aantal Weekblad', group: 'Kranten', slot: 'netto' }
]);
const BELGIUM_PC_SUMMARY_COLUMNS = Object.freeze([
  { key: 'addressCount', label: 'Addresses', group: 'Overheid' },
  { key: 'mailboxYesPub', label: 'Yes Pub', group: 'Mailbox' },
  { key: 'mailboxNoPub', label: 'No Pub', group: 'Mailbox' },
  { key: 'noPubPercentage', label: '% No Pub', group: 'Mailbox', aggregate: 'ratio' },
  { key: 'bdBruto', label: 'Bruto BD', group: 'BD' },
  { key: 'bdNetto', label: 'Netto BD', group: 'BD' }
]);
const ALL_PC_SUMMARY_COLUMNS = [...NETHERLANDS_PC_SUMMARY_COLUMNS, ...BELGIUM_PC_SUMMARY_COLUMNS];
const pcSummaryColumnVisibility = Object.fromEntries(ALL_PC_SUMMARY_COLUMNS.map(column => [column.key, true]));
let pcSummaryColumnSettingsOpen = false;
const PC_SUMMARY_PAGE_SIZE = 5;
const PC_SUMMARY_BLINK_COUNT = 3;
const PC_SUMMARY_BLINK_INTERVAL_MS = 180;
const PC_LAYER_COLORS = Object.freeze({
  pc4: '#FFA500',
  nis6: '#FF00FF',
  nis9: '#00ACC1',
  pc6: '#3B82F6'
});

function getPCSelectionColor(type) {
  return PC_LAYER_COLORS[normalizePCType(type)] || '#007BFF';
}

const pcSummaryBlinkStyle = [
  new Style({
    stroke: new Stroke({ color: '#ffffff', width: 9 }),
    image: new CircleStyle({
      radius: 11,
      fill: new Fill({ color: '#ffffff' }),
      stroke: new Stroke({ color: '#ffffff', width: 3 })
    })
  }),
  new Style({
    stroke: new Stroke({ color: '#2563eb', width: 5 }),
    fill: new Fill({ color: 'rgba(37, 99, 235, 0.4)' }),
    image: new CircleStyle({
      radius: 8,
      fill: new Fill({ color: 'rgba(37, 99, 235, 0.65)' }),
      stroke: new Stroke({ color: '#2563eb', width: 3 })
    })
  })
];

function notifyPCCurrentResultsLoaded(detail = {}) {
  document.dispatchEvent(new CustomEvent('pc-current-results-loaded', {
    detail
  }));
}

function setPCDialogActiveTool(selector) {
  if (selector) {
    setActiveSidebarTool('.pc-panel-toggle', { lock: true });
  } else {
    setSidebarToolActive('.pc-panel-toggle', false);
  }

  document
    .querySelectorAll('#pc-panel .pc4-select, #pc-panel .nis6-select, #pc-panel .pc6-select, #pc-panel .pc-polygon, #pc-panel .pc-circle')
    .forEach(button => {
      const active = Boolean(selector) && button.matches(selector);
      button.classList.toggle('active', active);
      button.classList.toggle('active-tool', active);
      button.classList.toggle('active-edit-vertex', active);
      button.setAttribute('aria-pressed', String(active));
      if (active) {
        button.style.setProperty('background', 'var(--sevenn-primary)', 'important');
        button.style.setProperty('background-color', 'var(--sevenn-primary)', 'important');
        button.style.setProperty('background-image', 'none', 'important');
        button.style.setProperty('border-color', 'var(--sevenn-primary)', 'important');
        button.style.setProperty('color', '#fff', 'important');
        button.style.setProperty('box-shadow', '0 8px 18px rgba(59, 57, 232, 0.24)', 'important');
      } else {
        button.style.removeProperty('background');
        button.style.removeProperty('background-color');
        button.style.removeProperty('background-image');
        button.style.removeProperty('border-color');
        button.style.removeProperty('color');
        button.style.removeProperty('box-shadow');
      }
    });
}

// Global selected features map: id => OL Feature
const selectedFeaturesMap = new Map();

function getPCAreaMessageData(feature) {
  const identifier = feature?.getId?.() ??
    feature?.get?.('id') ??
    feature?.get?.('s_source_id') ??
    null;

  return {
    areaType: feature?.get?.('_pcAreaType') || null,
    identifier,
    totalHouseholds: Number(feature?.get?.('u108212Aantal_VBO_BAG')) || 0,
    shape: geometryToShape(feature?.getGeometry?.())
  };
}

function sendPCAreaMessage(action, feature) {
  if (!feature) return;
  sendPostMessage(`pcarea:${action}`, getPCAreaMessageData(feature));
}

function sendPCAreaSnapshot(features = Array.from(selectedFeaturesMap.values())) {
  sendPostMessage('pcarea:snapshot', {
    areas: features.map(getPCAreaMessageData)
  });
}

function sendPCAreaRemovalMessages() {
  selectedFeaturesMap.forEach(feature => sendPCAreaMessage('remove', feature));
}

function parsePCSelectionProperties(value) {
  if (Array.isArray(value)) return value;
  if (!value) return [];

  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.warn('[PCSelection] Invalid saved properties JSON', value, error);
      return [];
    }
  }

  return [];
}

function getPCSelectionType(selection) {
  return String(
    selection?.type ??
    selection?.pcType ??
    selection?.pc_type ??
    selection?.areaType ??
    selection?.area_type ??
    selection?.level ??
    ''
  ).trim();
}

function getPCSelectionPropertiesPayload(selection) {
  if (!selection) return [];

  const directPayload =
    selection.properties ??
    selection.property ??
    selection.ids ??
    selection.features ??
    selection.selectedFeatures ??
    selection.selected_features ??
    selection.areas ??
    selection.areaIds ??
    selection.area_ids;

  const parsed = parsePCSelectionProperties(directPayload);
  if (parsed.length) return parsed;

  const singleId =
    selection.id ??
    selection.s_source_id ??
    selection.sourceId ??
    selection.source_id ??
    selection.featureId ??
    selection.feature_id;

  return singleId ? [{ id: singleId }] : [];
}

function getPCSelectionPropertyId(property) {
  if (property === null || property === undefined) return '';
  if (typeof property === 'string' || typeof property === 'number') return String(property).trim();
  return String(
    property.id ??
    property.s_source_id ??
    property.sourceId ??
    property.source_id ??
    property.featureId ??
    property.feature_id ??
    property.value ??
    ''
  ).trim();
}

function getPCSelectionLookupId(property, type) {
  if (!property || typeof property !== 'object') return getPCSelectionPropertyId(property);
  const level = normalizePCType(type);
  const candidates = level === 'nis9'
    ? [property.niscode, property.nis9]
    : level === 'nis6'
    ? [property.nis6, property.cd_sub_munty]
    : level === 'pc6' && isBelgiumPCSummary()
      ? [property['4pp'], property.nouveau_po, property.bd_code]
      : level === 'pc6'
        ? [property['6pp'], property.u104986pc6_cbs, property.pc6, property.nl_6pp, property.postcode6_BAG, property.u104723postcode6_BAG, property.postcode, property.postal_code]
        : level === 'pc4'
          ? [property['4pp'], property.u104987pc4_cbs, property.pc4, property.postcode4, property.postcode, property.postal_code]
          : [];
  const postcodeAlias = (level === 'pc4' || level === 'pc6')
    ? getPCSummaryPropertyValue([property], ['postcode', 'post_code', 'postalcode', 'postal_code', 'postcode6_BAG', 'u104723postcode6_BAG', 'u107260postcode'], [/postcode/, /postalcode/, /^pc[46]$/])
    : '';
  const value = [...candidates, postcodeAlias, getPCSelectionPropertyId(property)]
    .find(candidate => candidate !== undefined && candidate !== null && String(candidate).trim() !== '');
  if (value === undefined) return '';
  const lookupId = String(value).trim();
  return level === 'pc4' || level === 'pc6'
    ? lookupId.replace(/\s+/g, '').toUpperCase()
    : lookupId;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function getPCSelectionPropertyAddressCount(property) {
  if (!property || typeof property !== 'object') return 0;
  const value = Number(
    property.addressCount ??
    property.address_count ??
    property.total_mailbox ??
    property.count ??
    property.u108212Aantal_VBO_BAG
  );
  return Number.isFinite(value) ? value : 0;
}

function normalizeLoadedPCDrawSelections(selection, type) {
  const properties = getPCSelectionPropertiesPayload(selection);
  const baseSeq = selection.seq ?? selection.sequence ?? selection.displayOrder;
  const reservedSeqs = new Set(
    Array.from(pcDrawSelectionsByType.values())
      .flat()
      .map(item => Number(item.seq) || 0)
      .filter(Boolean)
  );
  const allocateReservedSeq = preferredSeq => {
    const preferred = Number(preferredSeq) || 0;
    if (preferred > 0 && !reservedSeqs.has(preferred)) {
      reservedSeqs.add(preferred);
      return preferred;
    }

    let nextSeq = 1;
    while (reservedSeqs.has(nextSeq)) {
      nextSeq += 1;
    }
    reservedSeqs.add(nextSeq);
    return nextSeq;
  };

  if (!properties.length) {
    return [{
      seq: allocateReservedSeq(baseSeq),
      color: getPCSelectionColor(type),
      count: Number(selection.count ?? selection.areaCount ?? selection.area_count) || 0,
      addressCount: Number(selection.addressCount ?? selection.address_count) || 0,
      properties: JSON.stringify([])
    }];
  }

  return properties.map((property, index) => ({
    seq: allocateReservedSeq(index === 0 ? baseSeq : null),
    color: getPCSelectionColor(type),
    featureId: getPCSelectionPropertyId(property),
    count: 1,
    addressCount: getPCSelectionPropertyAddressCount(property),
    properties: JSON.stringify([property])
  }));
}

function getPCFeatureAddressCount(feature) {
  const value = Number(
    (feature?.get?.('6pp') != null ? feature?.get?.('total_hh') : undefined)
    ?? (feature?.get?.('4pp') != null ? feature?.get?.('aantaladressen') : undefined)
    ?? feature?.get?.('total_mailbox')
    ?? feature?.get?.('address_count_sum')
    ?? feature?.get?.('aantaladressen')
    ?? feature?.get?.('u108212Aantal_VBO_BAG')
    ?? feature?.get?.('u108264Aantal_adres')
    ?? feature?.get?.('u108268Aantal_adres_bepost')
    ?? feature?.get?.('addressCount')
    ?? feature?.get?.('count')
  );
  return Number.isFinite(value) ? value : 0;
}

function getSelectedFeaturesAddressCount(features) {
  return (features || []).reduce((total, feature) => total + getPCFeatureAddressCount(feature), 0);
}

function getPCFeatureIdentifier(featureLike) {
  const properties = featureLike?.properties || featureLike?.getProperties?.() || {};
  const candidates = [
    featureLike?.id,
    properties['6pp'],
    properties.u104986pc6_cbs,
    properties.u104987pc4_cbs,
    properties.pc6,
    properties.pc5,
    properties.pc4,
    properties.nis6,
    properties.nis9,
    properties.niscode,
    properties['4pp'],
    properties.cd_sub_munty,
    properties.nouveau_po,
    properties.bd_code,
    properties.postcode,
    properties.postal_code,
    featureLike?.get?.('id'),
    featureLike?.get?.('s_source_id'),
    properties.id,
    properties.s_source_id,
    properties.objectid,
    properties.fid,
    properties.ID,
    featureLike?.getId?.()
  ];

  const value = candidates.find(candidate => candidate !== undefined && candidate !== null && String(candidate).trim() !== '');
  return value === undefined ? null : String(value);
}

function getNextPCDrawSeq() {
  return Array.from(pcDrawSelectionsByType.values())
    .flat()
    .reduce((max, item) => Math.max(max, Number(item.seq) || 0), 0) + 1;
}

function allocatePCDrawSeq(preferredSeq = null) {
  const seq = Number(preferredSeq) || 0;
  const used = new Set(
    Array.from(pcDrawSelectionsByType.values())
      .flat()
      .map(item => Number(item.seq) || 0)
      .filter(Boolean)
  );

  if (seq > 0 && !used.has(seq)) {
    return seq;
  }

  return getNextPCDrawSeq();
}

function normalizePCType(value) {
  return String(value || '').trim().toLowerCase();
}

function getPersistedPCType(value) {
  const type = normalizePCType(value);
  return type === 'nis6' ? 'pc5' : type;
}

function getRuntimePCType(value, countryId = getSessionContext()?.countryId) {
  const type = normalizePCType(value);
  if (type === 'nis9' && String(countryId) !== '4') return '';
  return String(countryId || '') === '4' && type === 'pc5' ? 'nis6' : type;
}

function isLocalPCSelectionPreview() {
  return ['127.0.0.1', 'localhost'].includes(window.location.hostname)
    && new URLSearchParams(window.location.search).get('pcSelectionPreview') === 'local';
}

function getLocalPCSelectionStorageKey(ctx) {
  return [
    'sevenn.pc-selections',
    ctx?.projectId || 'no-project',
    ctx?.countryId || 'no-country',
    ctx?.languageId || 'no-language'
  ].join(':');
}

function saveLocalPCSelections(ctx) {
  localStorage.setItem(
    getLocalPCSelectionStorageKey(ctx),
    JSON.stringify(getPCDrawSelections())
  );
}

function loadLocalPCSelections(ctx) {
  try {
    const value = localStorage.getItem(getLocalPCSelectionStorageKey(ctx));
    const parsed = value ? JSON.parse(value) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn('[PCSelection] Invalid local selection data', error);
    return [];
  }
}

function getAllPCLayers() {
  const layers = [];
  const currentMap = map || getMap();

  function recurse(layer) {
    if (!layer) return;
    if (layer.getLayers) {
      layer.getLayers().forEach(recurse);
      return;
    }

    if (getPCLevelForLayer(layer)) {
      layers.push(layer);
    }
  }

  currentMap?.getLayers?.().forEach(recurse);
  return layers;
}

function findPCFeatureById(type, id) {
  const normalizedType = String(type || '').trim().toUpperCase();
  const targetId = String(id || '').trim();
  if (!targetId) return null;

  const layers = normalizedType
    ? getSelectableLayers(normalizedType)
    : getAllPCLayers();

  for (const layer of layers) {
    const source = layer.get?.('wfsSource') || layer.getSource?.();
    // Rebuild once per source revision, rather than scanning every polygon
    // for every saved row during restoration and summary rendering.
    const revision = source?.getRevision?.();
    let found;
    if (Number.isFinite(revision)) {
      findPCFeatureById.indexes ||= new WeakMap();
      let indexes = findPCFeatureById.indexes.get(source);
      if (!indexes || indexes.revision !== revision) {
        indexes = { revision, byType: new Map() };
        findPCFeatureById.indexes.set(source, indexes);
      }
      let index = indexes.byType.get(normalizedType);
      if (!index) {
        index = new Map();
        (source.getFeatures?.() || []).forEach(feature => {
          const id = getPCFeatureIdentifier(feature);
          const lookupId = getPCSelectionLookupId({ ...feature.getProperties?.(), id }, type);
          if (id && !index.has(id)) index.set(id, feature);
          if (lookupId && !index.has(lookupId)) index.set(lookupId, feature);
        });
        indexes.byType.set(normalizedType, index);
      }
      found = index.get(targetId);
    } else {
      found = (source?.getFeatures?.() || []).find(feature => {
        const id = getPCFeatureIdentifier(feature);
        return id === targetId || getPCSelectionLookupId({ ...feature.getProperties?.(), id }, type) === targetId;
      });
    }
    if (found) return found;
  }

  return null;
}

function deduplicatePCDrawSelections(type) {
  const key = normalizePCType(type);
  const seen = new Set();
  const unique = (pcDrawSelectionsByType.get(key) || []).flatMap(selection => {
    const properties = getPCSelectionPropertiesPayload(selection).filter(property => {
      const id = getPCSelectionLookupId(property, key);
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
    return properties.length ? [{ ...selection, count: properties.length, properties: JSON.stringify(properties) }] : [];
  });
  if (unique.length) pcDrawSelectionsByType.set(key, unique);
  else pcDrawSelectionsByType.delete(key);
}

function getUnselectedPCFeatures(pcType, features) {
  const type = normalizePCType(pcType);
  const selectedIds = new Set();
  (pcDrawSelectionsByType.get(type) || []).forEach(selection => {
    getPCSelectionPropertiesPayload(selection).forEach(property => {
      const id = getPCSelectionPropertyId(property);
      const lookupId = getPCSelectionLookupId(property, type);
      if (id) selectedIds.add(id);
      if (lookupId) selectedIds.add(lookupId);
    });
  });

  return (features || []).filter(feature => {
    const id = getPCFeatureIdentifier(feature);
    if (!id) return false;
    const lookupId = getPCSelectionLookupId({ ...feature?.getProperties?.(), id }, type);
    if (selectedIds.has(id) || (lookupId && selectedIds.has(lookupId))) return false;
    selectedIds.add(id);
    if (lookupId) selectedIds.add(lookupId);
    return true;
  });
}

async function savePCDrawSelection(pcType, features) {
  const ctx = getSessionContext();
  const type = normalizePCType(pcType);

  if (!ctx?.countryId || !ctx?.languageId || !ctx?.projectId || !type) {
    console.warn('[PCSelection] Skipping PC draw save; missing context', { ctx, type });
    return;
  }

  if (type === 'nis9' && String(ctx.countryId) !== '4') {
    throw new Error('NIS9 selections are only available for Belgium.');
  }

  deduplicatePCDrawSelections(type);
  const newFeatures = getUnselectedPCFeatures(type, features);
  if (!newFeatures.length) return;

  const properties = newFeatures
    .map(feature => {
      const id = getPCFeatureIdentifier(feature);
      if (!id) return null;

      const geometryName = feature?.getGeometryName?.() || 'geometry';
      const scalarProperties = Object.fromEntries(
        Object.entries(feature?.getProperties?.() || {}).filter(([key, value]) =>
          key !== 'geometry'
          && key !== geometryName
          && (value === null || ['string', 'number', 'boolean'].includes(typeof value))
        )
      );

      return {
        ...scalarProperties,
        id,
        addressCount: getPCFeatureAddressCount(feature)
      };
    })
    .filter(Boolean);

  if (!properties.length) {
    console.warn('[PCSelection] Skipping PC draw save; no PC feature identifiers found');
    return;
  }

  const color = getPCSelectionColor(type);
  const selections = [...(pcDrawSelectionsByType.get(type) || [])];
  const entry = {
    seq: allocatePCDrawSeq(),
    color,
    count: properties.length,
    addressCount: getSelectedFeaturesAddressCount(newFeatures),
    properties: JSON.stringify(properties)
  };

  selections.push(entry);
  pcDrawSelectionsByType.set(type, selections);

  try {
    await persistPCDrawSelections(type);
  } catch (error) {
    // An unsuccessful save must not leave a temporary row that disappears
    // on refresh, or gets included in another selection's save later.
    const remaining = (pcDrawSelectionsByType.get(type) || []).filter(selection => selection !== entry);
    if (remaining.length) pcDrawSelectionsByType.set(type, remaining);
    else pcDrawSelectionsByType.delete(type);
    throw error;
  }
  return { ...entry, type };
}

async function persistPCDrawSelections(pcType) {
  const ctx = getSessionContext();
  const type = normalizePCType(pcType);
  const selections = pcDrawSelectionsByType.get(type) || [];

  if (!ctx?.countryId || !ctx?.languageId || !ctx?.projectId || !type) {
    throw new Error('Cannot save PC draw selections; missing session context or type');
  }

  const serializeProperties = selection => JSON.stringify(
    getPCSelectionPropertiesPayload(selection).map(property => {
      if (!property || typeof property !== 'object') return property;
      const place = (String(ctx.countryId) === '1' && type === 'pc4' ? property.woonplaats : undefined)
        ?? property.place ?? property.city ?? property.locality ?? property.name ?? '';
      const postcode = property.postcode ?? property.postal_code ?? property.code ?? property.id ?? '';
      const addressCount = (String(ctx.countryId) === '4' && type === 'pc6' ? property.aantaladressen : undefined)
        ?? (String(ctx.countryId) === '1' && type === 'pc4' ? property.address_count_sum : undefined)
        ?? property.addressCount ?? property.address_count ?? property.addresses ?? property.count ?? 0;
      return {
        ...property,
        place,
        city: property.city ?? place,
        postcode,
        addressCount: Number(addressCount) || 0
      };
    })
  );

  const payload = {
    projectId: ctx.projectId,
    // The deployed API still validates this Belgium level as `pc5`. Keep
    // that legacy value at the transport boundary only; runtime state remains
    // NIS6 throughout the map and UI.
    type: getPersistedPCType(type),
    selections: selections.map(selection => ({
      seq: Number(selection.seq) || 0,
      color: getPCSelectionColor(type),
      count: Number(selection.count) || 0,
      addressCount: Number(selection.addressCount) || 0,
      properties: serializeProperties(selection)
    }))
  };

  if (isLocalPCSelectionPreview()) {
    saveLocalPCSelections(ctx);
    return;
  }

  const response = await secureFetch(
    `${config.API_URL}spatial-search/pcdraw/save?countryId=${encodeURIComponent(ctx.countryId)}&languageId=${encodeURIComponent(ctx.languageId)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    }
  );

  if (!response.ok) {
    const text = await response.text();
    throw new Error(text || `PC draw save failed with status ${response.status}`);
  }
  await refreshPCSummaryMetadataFromBackend(type, ctx);
}

async function refreshPCSummaryMetadataFromBackend(type, ctx) {
  if (String(ctx.countryId) !== '1' || !['pc4', 'pc6'].includes(type)) return;
  if (!getPCDrawSelections().some(selection => normalizePCType(selection.type) === type)) return;
  try {
    const response = await secureFetch(
      `${config.API_URL}spatial-search/pcdraw/load?countryId=${encodeURIComponent(ctx.countryId)}&languageId=${encodeURIComponent(ctx.languageId)}`
    );
    if (!response.ok) throw new Error(`Summary metadata refresh failed (HTTP ${response.status})`);
    const rows = await response.json();
    if (!Array.isArray(rows)) throw new Error('Summary metadata refresh returned invalid selections');
    if (String(getSessionContext()?.projectId) !== String(ctx.projectId)) return;
    const metadata = new Map();
    rows.filter(row => normalizePCType(getPCSelectionType(row)) === type).forEach(row => {
      getPCSelectionPropertiesPayload(row).forEach(property => {
        metadata.set(normalizePostcodeValue(getPCSelectionLookupId(property, type)), property);
        metadata.set(normalizePostcodeValue(getPCSelectionPropertyId(property)), property);
      });
    });
    const current = pcDrawSelectionsByType.get(type);
    if (!current) return;
    pcDrawSelectionsByType.set(type, current.map(selection => ({
      ...selection,
      properties: JSON.stringify(getPCSelectionPropertiesPayload(selection).map(property => {
        const enriched = metadata.get(normalizePostcodeValue(getPCSelectionLookupId(property, type)))
          || metadata.get(normalizePostcodeValue(getPCSelectionPropertyId(property)));
        if (!enriched) return property;
        const place = (type === 'pc4' ? enriched.woonplaats : enriched.gemeente)
          || enriched.place || enriched.city
          || (type === 'pc4' ? property.woonplaats : property.gemeente) || property.place || property.city || '';
        const count = (type === 'pc4' ? enriched.address_count_sum : enriched.total_hh)
          ?? enriched.addressCount ?? enriched.address_count;
        return {
          ...property, place, city: place,
          ...(type === 'pc4' ? { woonplaats: place } : { gemeente: place }),
          ...(count !== undefined && count !== null ? {
            addressCount: Number(count) || 0,
            [type === 'pc4' ? 'address_count_sum' : 'total_hh']: Number(count) || 0
          } : {})
        };
      }))
    })));
    // Update the existing panel only; a delayed response must not reopen it.
    if (pcSummaryPanel) renderPCSummaryPanel();
  } catch (error) {
    // The save succeeded; metadata failure must not undo the selection.
    console.warn('Area Selection summary metadata refresh failed:', error);
  }
}

async function trySavePCDrawSelection(pcType, features) {
  try {
    const savedSelection = await savePCDrawSelection(pcType, features);
    if (!savedSelection) return 0;
    const savedIds = new Set(getPCSelectionPropertiesPayload(savedSelection).map(getPCSelectionPropertyId));
    highlightPCDrawSelectionFeatures(savedSelection, features.filter(feature => savedIds.has(getPCFeatureIdentifier(feature))));
    renderPCSummaryPanel();
    return savedSelection.count;
  } catch (error) {
    console.error('PC draw save failed:', error);
    setStatus(`${pcType} selection could not be saved. Please try again.`);
    return null;
  }
}

/** ================================
 * Styles
 * ================================ */
const highlightStyle = new Style({
  stroke: new Stroke({ color: '#007bff', width: 2 }),
  fill: new Fill({ color: 'rgba(0, 123, 255, 0.1)' }),
});

const drawStyle = new Style({
  fill: new Fill({ color: 'rgba(0, 123, 255, 0.1)' }),
  stroke: new Stroke({ color: '#007bff', width: 2 }),
  image: new CircleStyle({ radius: 5, fill: new Fill({ color: '#007bff' }) }),
});

let map = null;
let externalCancelBound = false;

/** ================================
 * Utility
 * ================================ */
const PC_SELECTION_LEVELS = ['PC4', 'NIS6', 'PC6', 'NIS9'];
const PC_SELECTION_LEVEL_ALIASES = {
  PC4: ['PC4', 'POSTCODE4', 'POSTALCODE4'],
  NIS6: ['NIS6'],
  NIS9: ['NIS9'],
  PC6: ['PC6', 'POSTCODE6', 'POSTALCODE6']
};
const ADDRESS_LAYER_ALIASES = ['ADDRESS', 'ADDRESSES'];
const BELGIUM_COUNTRY_CODE = 'be';
const BELGIUM_NIS6_TYPE_NAME = 'GEOLINQ:BE_DEELGEMEENTEN';
const BELGIUM_POSTALCODE_TYPE_NAME = 'GEOLINQ:BE_4PP';

function isBelgiumPCSummary() {
  return getSelectedCountryAbbreviation?.() === BELGIUM_COUNTRY_CODE
    || String(getSessionContext()?.countryId || '') === '4';
}

function getPCSummaryColumns() {
  return isBelgiumPCSummary() ? BELGIUM_PC_SUMMARY_COLUMNS : NETHERLANDS_PC_SUMMARY_COLUMNS;
}

function setCursor(cursor) {

  if (!map) return;
    const el = map.getTargetElement();
  if (el) {
    el.style.cursor = cursor;
    el.classList.toggle('pc-feature-selection-active', cursor === 'pointer');
  }
}

function getLayerTitle(layer) {
  return String(layer?.get?.('title') || layer?.get?.('name') || '').trim().toUpperCase();
}

function getLayerTypeName(layer) {
  return String(layer?.get?.('typeName') || '').trim().toUpperCase();
}

function normalizeLayerKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^layers\.base\./, '')
    .replace(/^geolinq:/, '')
    .replace(/\([^)]*\)/g, '')
    .replace(/[^a-z0-9]/g, '');
}

function getPCLevelForLayer(layer) {
  const sourceName = String(layer?.get?.('typeName') || '').trim().toUpperCase();
  if (sourceName === 'GEOLINQ:NL_4PP') return 'PC4';
  if (sourceName === 'GEOLINQ:NL_6PP') return 'PC6';
  const title = getLayerTitle(layer).replace(/[\s_-]+/g, '');
  const typeName = getLayerTypeName(layer);
  const isNIS9 = title === 'NIS9' || typeName === 'GEOLINQ:BE_STATISTICAL_SECTORS'
    || normalizeLayerKey(layer?.get?.('addressAidsRole')) === 'nis9';
  if (isNIS9 && String(getSessionContext()?.countryId) !== '4') return null;

  // The loaded layer identity is authoritative here. Country option metadata
  // may not yet contain the `be` abbreviation while layers are being rebuilt.
  if (title === 'NIS6' || typeName === BELGIUM_NIS6_TYPE_NAME) {
    return 'NIS6';
  }

  if (
    title === 'POSTALCODE'
    || title === 'POSTALCODES'
    || typeName === BELGIUM_POSTALCODE_TYPE_NAME
  ) {
    return 'PC6';
  }

  if (title === 'NIS9' || typeName === 'GEOLINQ:BE_STATISTICAL_SECTORS') {
    return 'NIS9';
  }

  const role = normalizeLayerKey(layer?.get?.('addressAidsRole'));
  if (PC_SELECTION_LEVELS.includes(role.toUpperCase())) {
    return role.toUpperCase();
  }

  const levelFromTitle = PC_SELECTION_LEVELS.find(level =>
    PC_SELECTION_LEVEL_ALIASES[level].some(alias => title === alias)
  ) || null;

  if (levelFromTitle) return levelFromTitle;
  return null;
}

function isAddressLayer(layer) {
  const role = normalizeLayerKey(layer?.get?.('addressAidsRole'));
  if (role === 'address' || role === 'addresses') return true;

  const title = normalizeLayerKey(layer?.get?.('title') || layer?.get?.('name'));
  return ADDRESS_LAYER_ALIASES.some(alias => title === normalizeLayerKey(alias));
}

function isLayerVisibleAtResolution(layer, resolution, zoom) {
  if (!layer?.getVisible?.()) return false;
  if (layer.get?.('disabledByRights')) return false;
  if (layer.get?.('visibleAtCurrentZoom') === true) return true;

  return isViewInLayerVisibilityRange(layer, resolution, zoom);
}

function collectVisiblePCLevels(layerCollection, resolution, zoom, parentVisible = true, visibleLevels = new Set()) {
  layerCollection?.forEach(layer => {
    const pcLevel = getPCLevelForLayer(layer);
    const layerVisible = parentVisible && isLayerVisibleAtResolution(layer, resolution, zoom);

    if (pcLevel && layerVisible) {
      visibleLevels.add(pcLevel);
    }

    if (typeof layer.getLayers === 'function') {
      collectVisiblePCLevels(layer.getLayers(), resolution, zoom, layerVisible, visibleLevels);
    }
  });

  return visibleLevels;
}

function collectConfiguredPCLevels(layerCollection, configuredLevels = new Set()) {
  layerCollection?.forEach(layer => {
    const pcLevel = getPCLevelForLayer(layer);
    const availableInLayerInfo =
      !layer.get?.('disabledByRights') &&
      Boolean(String(layer.get?.('title') || '').trim());

    if (pcLevel && availableInLayerInfo) {
      configuredLevels.add(pcLevel);
    }

    if (typeof layer.getLayers === 'function') {
      collectConfiguredPCLevels(layer.getLayers(), configuredLevels);
    }
  });

  return configuredLevels;
}

function collectVisibleAddressLayers(layerCollection, resolution, zoom, parentVisible = true, visibleLayers = new Set()) {
  layerCollection?.forEach(layer => {
    const layerVisible = parentVisible && isLayerVisibleAtResolution(layer, resolution, zoom);

    if (layerVisible && isAddressLayer(layer)) {
      visibleLayers.add(getLayerTitle(layer) || 'ADDRESSES');
    }

    if (typeof layer.getLayers === 'function') {
      collectVisibleAddressLayers(layer.getLayers(), resolution, zoom, layerVisible, visibleLayers);
    }
  });

  return visibleLayers;
}

function getConfiguredBelgiumSelectionLayers() {
  const selectionLayers = [];

  function collect(layer) {
    if (typeof layer?.getLayers === 'function') {
      layer.getLayers().forEach(collect);
      return;
    }

    const level = getPCLevelForLayer(layer);
    const typeName = getLayerTypeName(layer);
    if (
      level === 'NIS6'
      || level === 'NIS9'
      || typeName === BELGIUM_POSTALCODE_TYPE_NAME
    ) {
      selectionLayers.push({ layer, level });
    }
  }

  window.base_map?.getLayers?.().forEach(collect);
  return selectionLayers;
}

function isBelgiumSelectionLayerActuallyVisible(layer, resolution, zoom) {
  return !!layer?.getVisible?.()
    && layer.get?.('backendVisible') !== false
    && !layer.get?.('disabledByRights')
    && isViewInLayerVisibilityRange(layer, resolution, zoom);
}

export function getPCSelectionAvailability(map) {
  if (!hasRight('data.pcSelection')) {
    return {
      allowed: false,
      configuredLevels: new Set(),
      visibleLevels: new Set(),
      visibleAddressLayers: new Set(),
      reason: 'Area selection permission is required.'
    };
  }
  if (!map?.getView) {
    return {
      allowed: false,
      configuredLevels: new Set(),
      visibleLevels: new Set(),
      reason: 'Map is not ready.'
    };
  }

  const resolution = map.getView().getResolution();
  const zoom = map.getView().getZoom?.();
  const configuredLevels = collectConfiguredPCLevels(window.base_map?.getLayers?.());
  const visibleLevels = collectVisiblePCLevels(map.getLayers(), resolution, zoom);
  const visibleAddressLayers = collectVisibleAddressLayers(map.getLayers(), resolution, zoom);
  const belgiumSelectionLayers = getConfiguredBelgiumSelectionLayers();

  // Belgium Area Selection follows whichever supported administrative layer
  // is actually displayed: NIS6, NIS9 or PostalCode. Addresses alone must not
  // enable these tools.
  if (belgiumSelectionLayers.length) {
    configuredLevels.clear();
    visibleLevels.clear();
    visibleAddressLayers.clear();

    belgiumSelectionLayers.forEach(({ layer, level }) => {
      if (layer.get?.('disabledByRights')) return;
      configuredLevels.add(level);
      if (isBelgiumSelectionLayerActuallyVisible(layer, resolution, zoom)) {
        visibleLevels.add(level);
      }
    });
  }

  if (!visibleLevels.size && !visibleAddressLayers.size) {
    return {
      allowed: false,
      configuredLevels,
      visibleLevels,
      visibleAddressLayers,
      reason: translate('pc_selection.unavailable')
    };
  }

  const contexts = [
    ...visibleLevels,
    ...visibleAddressLayers
  ];

  return {
    allowed: true,
    configuredLevels,
    visibleLevels,
    visibleAddressLayers,
    reason: `Area Selections available using ${contexts.join(', ')}.`
  };
}

// Use the configured country layer title rather than the internal selection level.
function getPCSelectionLayerTitle(level) {
  let title = '';
  function collect(layers) {
    layers?.forEach(layer => {
      if (!title && getPCLevelForLayer(layer) === level && !layer.get?.('disabledByRights')) {
        title = String(layer.get?.('title') || layer.get?.('name') || '').trim();
      }
      if (typeof layer.getLayers === 'function') collect(layer.getLayers());
    });
  }
  collect(window.base_map?.getLayers?.());
  return title || level;
}

let lastPCSelectionAvailabilityAllowed = null;

// State updates can run before the rights-driven feature initializer. Bind the
// controls here as well, once per element, so enabled tools always have handlers.
export function initPCSelectionControls() {
  const actions = [
    ['.pc4-select', () => activatePCSelection('PC4')],
    ['.nis6-select', () => activatePCSelection('NIS6')],
    ['.pc6-select', () => activatePCSelection('PC6')],
    ['.pc-polygon', drawPCPolygon],
    ['.pc-circle', drawPCCircle],
    ['.pc-current-results', () => loadPCDrawFromBackend({ fitToResults: true, openPanel: true, showImmediately: true })]
  ];
  actions.forEach(([selector, action]) => {
    const button = document.querySelector(`#pc-panel ${selector}`);
    if (!button || button.__sevennPCActionBound) return;
    button.__sevennPCActionBound = true;
    button.type = 'button';
    button.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      // Reading the authenticated user's current results is independent of
      // permission to create new selections (startup already restores them).
      if (button.disabled || (selector !== '.pc-current-results' && !hasRight('data.pcSelection'))) return;
      action();
    });
  });
}

export function updatePCSelectionButtons(map) {
  initPCSelectionControls();
  const availability = getPCSelectionAvailability(map);
  const drawAllowed = availability.allowed;

  [
    { selector: '.pc4-select', level: 'PC4', titleKey: 'selection_aids.pc4' },
    { selector: '.nis6-select', level: 'NIS6', titleKey: 'selection_aids.pc5' },
    { selector: '.pc6-select', level: 'PC6', titleKey: 'selection_aids.pc6' }
  ].forEach(({ selector, level, titleKey }) => {
    const button = document.querySelector(selector);
    if (!button) return;
    const tool = button.closest('.pc-selection-tool');
    const label = translate(titleKey, { Area: getPCSelectionLayerTitle(level) });
    // Availability updates own these dynamic tooltips.
    button.removeAttribute('data-translate-title');

    const configured = availability.configuredLevels.has(level);
    const active = availability.visibleLevels.has(level);
    button.style.display = configured ? '' : 'none';
    button.hidden = !configured;
    if (tool) {
      tool.style.display = configured ? '' : 'none';
      tool.hidden = !configured;
    }

    if (!configured) {
      button.disabled = true;
      button.classList.add('disabled');
      button.classList.remove('pc-level-available');
      button.classList.add('pc-level-blocked');
      button.setAttribute('aria-disabled', 'true');
      return;
    }

    button.disabled = !active;
    button.classList.toggle('disabled', !active);
    button.classList.toggle('pc-level-available', active);
    button.classList.toggle('pc-level-blocked', !active);
    button.setAttribute('aria-disabled', String(!active));
    const title = active ? label : translate('pc_selection.unavailable');
    updateToolAvailabilityTooltip(button, title);
  });

  [
    { selector: '.pc-polygon', titleKey: 'selection_aids.draw_polygon' },
    { selector: '.pc-circle', titleKey: 'selection_aids.draw_circle' }
  ].forEach(({ selector, titleKey }) => {
    const button = document.querySelector(selector);
    if (!button) return;
    const label = translate(titleKey);
    const title = drawAllowed ? label : availability.reason;

    button.disabled = !drawAllowed;
    button.classList.toggle('disabled', !drawAllowed);
    button.classList.toggle('pc-draw-available', drawAllowed);
    button.classList.toggle('pc-draw-blocked', !drawAllowed);
    button.setAttribute('aria-disabled', String(!drawAllowed));
    updateToolAvailabilityTooltip(button, title);
  });

  if ((activePCSelectionLevel && !availability.visibleLevels.has(activePCSelectionLevel)
      || lastPCSelectionAvailabilityAllowed === true && !availability.allowed) &&
      (pcSelectInteraction || pcDrawInteraction || pcDrawSessionActive)) {
    stopPCSelectionInteraction();
    setStatus(availability.reason);
    setCommand("Area Selections unavailable");
  }

  lastPCSelectionAvailabilityAllowed = availability.allowed;

  return availability;
}

export function canUsePCSelectionDraw(map, notify = true) {
  const availability = updatePCSelectionButtons(map);

  if (!availability.allowed && notify) {
    import('./mapUtils.js').then(MapUtils => {
      MapUtils.setStatus(availability.reason);
      MapUtils.setCommand("Area Selections unavailable");
    });
  }

  return availability.allowed;
}

function bindPcEscapeHandlerOnce() {
  if (pcEscapeHandlerBound) return;

  pcEscapeHandler = evt => {
    if (evt.key !== 'Escape') return;

    if (pcDrawInteraction || pcDrawSessionActive) {
      cancelPCDrawSession();
      return;
    }

    deactivatePCSelection();
  };

  document.addEventListener('keydown', pcEscapeHandler);
  pcEscapeHandlerBound = true;
}

function bindExternalCancelOnce() {
  if (externalCancelBound) return;

  document.addEventListener('address-aids-activating', () => {
    stopPCSelectionInteraction();
  });

  externalCancelBound = true;
}

function notifyPCSelectionActivating() {
  document.dispatchEvent(new CustomEvent('pc-selection-activating'));
}

function preserveLegendDuringAreaSelectionActivation() {
  const messageEl = document.getElementById('message-text');
  const legendHtml = messageEl?.innerHTML || '';

  resetActiveCommandsOnly({
    preserveAddressAidsResults: true,
    legendSuspendMs: 1200
  });

  if (!messageEl || !legendHtml) return;

  const restoreLegend = () => {
    if (!messageEl.isConnected) return;
    messageEl.innerHTML = legendHtml;
  };

  restoreLegend();
  window.setTimeout(restoreLegend, 100);
  window.setTimeout(restoreLegend, 350);
}

function cancelPCDrawSession() {
  pcDrawSessionActive = false;

  if (pcDrawInteraction) {
    try {
      pcDrawInteraction.abortDrawing();
    } catch (_) {
      // ignore abort failures
    }
  }

  stopPCSelectionInteraction();
  stopDrawAutoPan();

  if (vecSource) {
    vecSource.clear();
  }

  setCursor('');
  setCommand('Ready');
  updateSelectionStatus();
  setPCDialogActiveTool(null);
}


// Update status text from global selectedFeaturesMap
function updateSelectionStatus() {
  let totalAddresses = 0;

  selectedFeaturesMap.forEach(f => {
    
    totalAddresses += Number(f.get('u108212Aantal_VBO_BAG')) || 0;
  });

  setStatus(`${selectedFeaturesMap.size} PCs selected | ${totalAddresses} addresses`);
}

/** ================================
 * Toggle a feature in the global selection
 * ================================ */
function toggleFeatureSelection(feature, areaType = null) {
  if (!feature) return;

  // Determine stable id
  const fid = feature.getId?.() || feature.get('id') || JSON.stringify(feature.getProperties());

  if (selectedFeaturesMap.has(fid)) {
    const removedFeature = selectedFeaturesMap.get(fid);
    selectedFeaturesMap.delete(fid);
    sendPCAreaMessage('remove', removedFeature);
  } else {
    // clone feature to avoid style conflicts
    const clone = feature.clone();
    clone.setId(fid);
    clone.setStyle(createStyleFromColor(getPCSelectionColor(areaType)));
    clone.set('_pcAreaType', areaType);
    selectedFeaturesMap.set(fid, clone);
    sendPCAreaMessage('add', clone);
  }

  // Update highlight layer
  const selLayer = getVecSelectionLayer();
  const selSrc = selLayer.getSource();
  selSrc.clear();
  selSrc.addFeatures(Array.from(selectedFeaturesMap.values()));

  updateSelectionStatus();
}

function addFeatureToSelection(feature, areaType = null) {
  if (!feature) return false;

  const fid = feature.getId?.() || feature.get('id') || JSON.stringify(feature.getProperties());
  if (selectedFeaturesMap.has(fid)) return false;

  const clone = feature.clone();
  clone.setId(fid);
  clone.setStyle(createStyleFromColor(getPCSelectionColor(areaType)));
  clone.set('_pcAreaType', areaType);
  selectedFeaturesMap.set(fid, clone);

  const selLayer = getVecSelectionLayer();
  selLayer?.getSource()?.addFeature(clone);
  sendPCAreaMessage('add', clone);
  return true;
}

/** ================================
 * Vector Layer for selected features
 * ================================ */
let vecSelectionLayer = null;
const pcSelectedAreaVisibility = new Map(PC_SELECTION_LEVELS.map(type => [type, true]));
const pcSelectedAreaFeatureCache = new Map(PC_SELECTION_LEVELS.map(type => [type, new Map()]));
function applyPCSelectedAreaVisibility(feature) {
  const type = String(feature?.get?.('_pcAreaType') || '').toUpperCase();
  if (!PC_SELECTION_LEVELS.includes(type)) return;
  feature.setStyle(pcSelectedAreaVisibility.get(type) === false ? null : createStyleFromColor(getPCSelectionColor(type)));
}
function getVecSelectionLayer() {

  if (!map) map = getMap();
  if (!map) return null;

  if (!vecSelectionLayer) {
    vecSelectionLayer = new VectorLayer({
      source: new VectorSource(),
      style: highlightStyle,
      zIndex: 9999
    });
    vecSelectionLayer.set('selectedAreasKind', 'pc');
    vecSelectionLayer.set('getSelectedAreaEntries', () => PC_SELECTION_LEVELS.map(type => ({ key: type.toLowerCase(), label: `${type} Selection`, color: getPCSelectionColor(type), visible: pcSelectedAreaVisibility.get(type) !== false, count: (pcDrawSelectionsByType.get(type.toLowerCase()) || []).reduce((total, selection) => total + getPCSelectionPropertiesPayload(selection).length, 0) })).filter(entry => entry.count > 0));
    vecSelectionLayer.set('setSelectedAreaVisibility', (type, visible) => {
      const normalizedType = String(type || '').toUpperCase();
      pcSelectedAreaVisibility.set(normalizedType, !!visible);
      const source = vecSelectionLayer.getSource();
      const cache = pcSelectedAreaFeatureCache.get(normalizedType) || new Map(); pcSelectedAreaFeatureCache.set(normalizedType, cache);
      if (!visible) {
        source.getFeatures().filter(feature => String(feature.get('_pcAreaType') || '').toUpperCase() === normalizedType).forEach(feature => { cache.set(feature.getId(), feature); selectedFeaturesMap.delete(feature.getId()); sendPCAreaMessage('remove', feature); source.removeFeature(feature); });
        closePCSummaryPanel();
      } else {
        Array.from(cache.values()).forEach(feature => { applyPCSelectedAreaVisibility(feature); selectedFeaturesMap.set(feature.getId(), feature); source.addFeature(feature); sendPCAreaMessage('add', feature); }); cache.clear();
      }
      source.getFeatures().forEach(feature => {
        if (String(feature.get('_pcAreaType') || '').toUpperCase() !== normalizedType) return;
        const featureId = feature.getId();
        if (visible && !selectedFeaturesMap.has(featureId)) selectedFeaturesMap.set(featureId, feature);
        applyPCSelectedAreaVisibility(feature);
      });
      vecSelectionLayer.changed();
      updateSelectionStatus();
      sendPCAreaSnapshot();
      document.dispatchEvent(new CustomEvent('selected-areas-updated'));
    });
    vecSelectionLayer.set('zoomToSelectedArea', type => {
      const normalizedType = String(type || '').toUpperCase();
      const features = vecSelectionLayer.getSource().getFeatures().filter(feature => String(feature.get('_pcAreaType') || '').toUpperCase() === normalizedType).concat(Array.from(pcSelectedAreaFeatureCache.get(normalizedType)?.values() || []));
      fitPCSelectionLayerToFeatures(features);
      return features.length > 0;
    });
    vecSelectionLayer.getSource().on(['addfeature', 'removefeature', 'clear'], event => {
      if (event.feature) {
        applyPCSelectedAreaVisibility(event.feature);
        const type = String(event.feature.get('_pcAreaType') || '').toUpperCase();
        if (pcSelectedAreaVisibility.get(type) === false && selectedFeaturesMap.has(event.feature.getId())) { selectedFeaturesMap.delete(event.feature.getId()); sendPCAreaMessage('remove', event.feature); updateSelectionStatus(); sendPCAreaSnapshot(); }
      }
      document.dispatchEvent(new CustomEvent('selected-areas-updated'));
    });
    map.addLayer(vecSelectionLayer);
  }
  return vecSelectionLayer;
}

function getAllVectorLayers(layers, pcType) {
  const pcTypeLower = pcType.toLowerCase();
  const all = [];

  layers.forEach(layer => {
    if (layer.getLayers) {
      all.push(...getAllVectorLayers(layer.getLayers(), pcType)); // recurse
    } else if (layer.get('title')?.toLowerCase() === pcTypeLower) {

      all.push(layer);
    }
  });

  return all;
}


function getSelectableLayers(pcType) {
  const targetPCType = String(pcType || '').trim().toUpperCase();
  const layers = [];
  const currentMap = map || getMap();
  if (!currentMap) return layers;

  function recurse(layer) {
    if (layer.getLayers) {
      layer.getLayers().forEach(recurse);
    } else if (getPCLevelForLayer(layer) === targetPCType) {
      layers.push(layer);
    }
  }

  currentMap.getLayers().forEach(recurse);

  return layers;
}

function selectVisiblePCFeaturesFromMap(drawGeometry, pcType, options = {}) {
  if (!map || !drawGeometry) return [];

  const extent = drawGeometry.getExtent();
  const selectedFeatures = [];

  getSelectableLayers(pcType).forEach(layer => {
    if (layer.get?.('disabledByRights') === true || (!options.includeHidden && !layer.getVisible?.())) return;

    const source = layer.get?.('wfsSource') || layer.getSource?.();
    if (!source) return;

    const candidates = typeof source.getFeaturesInExtent === 'function'
      ? source.getFeaturesInExtent(extent)
      : source.getFeatures?.() || [];

    candidates.forEach(feature => {
      const geometry = feature.getGeometry?.();
      if (!geometry?.intersectsExtent?.(extent)) return;
      selectedFeatures.push(feature);
    });
  });

  updateSelectionStatus();
  return selectedFeatures;
}

async function selectLoadedPCFeaturesForDraw(drawGeometry, pcType, options = {}) {
  if (!map || !drawGeometry) return [];
  const view = map.getView();
  const sources = new Set();
  getSelectableLayers(pcType).forEach(layer => {
    if (layer.get?.('disabledByRights') === true || (!options.includeHidden && !layer.getVisible?.())) return;
    const source = layer.get?.('wfsSource') || layer.getSource?.();
    if (source) sources.add(source);
  });
  await Promise.all([...sources].map(source =>
    waitForPCDrawSource(source, drawGeometry.getExtent(), view)
  ));
  return selectVisiblePCFeaturesFromMap(drawGeometry, pcType, options);
}

function waitForPCDrawSource(source, extent, view) {
  return new Promise((resolve, reject) => {
    let timeout;
    const finish = error => {
      clearTimeout(timeout);
      source.un('featuresloadend', onLoad);
      source.un('featuresloaderror', onLoad);
      if (error) reject(error);
      else resolve();
    };
    const onLoad = () => {
      if (!source.loading) finish();
    };
    source.on('featuresloadend', onLoad);
    source.on('featuresloaderror', onLoad);
    // Dense NIS9 responses can take longer than the former two-second poll.
    // Keep drawing bounded if a request never completes.
    timeout = setTimeout(finish, 15000);
    try {
      source.loadFeatures?.(extent, view.getResolution(), view.getProjection());
      onLoad(); // Cached extents do not emit another load event.
    } catch (error) {
      finish(error);
    }
  });
}

function upsertPCDrawSelection(type, selection) {
  const key = normalizePCType(type);
  const selections = pcDrawSelectionsByType.get(key) || [];
  const existingIndex = selections.findIndex(item => Number(item.seq) === Number(selection.seq));
  const normalizedSelection = {
    ...selection,
    color: getPCSelectionColor(key)
  };

  if (existingIndex >= 0) {
    selections[existingIndex] = normalizedSelection;
  } else {
    selections.push(normalizedSelection);
  }

  selections.sort((a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0));
  pcDrawSelectionsByType.set(key, selections);
}

function setPCDrawSelectionAddressCount(type, seq, addressCount) {
  const key = normalizePCType(type);
  const selections = pcDrawSelectionsByType.get(key) || [];
  const existingIndex = selections.findIndex(item => Number(item.seq) === Number(seq));

  if (existingIndex < 0) return;

  selections[existingIndex] = {
    ...selections[existingIndex],
    addressCount: Number(addressCount) || 0
  };
  pcDrawSelectionsByType.set(key, selections);
}

function getSelectionByTypeAndSeq(type, seq) {
  const key = normalizePCType(type);
  const selections = pcDrawSelectionsByType.get(key) || [];
  return selections.find(item => Number(item.seq) === Number(seq)) || null;
}

function updatePCDrawSelectionColor(type, seq) {
  const key = normalizePCType(type);
  const selections = pcDrawSelectionsByType.get(key) || [];
  const existingIndex = selections.findIndex(item => Number(item.seq) === Number(seq));

  if (existingIndex < 0) return null;

  const nextColor = getPCSelectionColor(key);
  if (!nextColor) return null;

  const updatedSelection = {
    ...selections[existingIndex],
    color: nextColor
  };

  selections[existingIndex] = updatedSelection;
  pcDrawSelectionsByType.set(key, selections);

  const prefix = `${key}:${Number(seq) || 0}:`;
  selectedFeaturesMap.forEach(feature => {
    if (!String(feature?.getId?.() || '').startsWith(prefix)) return;
    feature.setStyle(createStyleFromColor(nextColor));
  });

  const selectionLayer = getVecSelectionLayer();
  const source = selectionLayer?.getSource?.();
  source?.changed?.();

  return nextColor;
}

function getPCDrawSelections() {
  return Array.from(pcDrawSelectionsByType.entries()).flatMap(([type, selections]) =>
    selections.map(selection => ({ ...selection, type }))
  ).sort((a, b) => (Number(a.seq) || 0) - (Number(b.seq) || 0));
}

async function exportPCDrawSelections(downloadBtn) {
  const rows = getPCDrawSelections();
  const ctx = getSessionContext();

  if (!rows.length) {
    alert(translate('export.no_features_selected_map'));
    return;
  }

  if (!ctx?.projectId) {
    alert(translate('export.no_exportable_data'));
    return;
  }

  downloadBtn.disabled = true;
  showLoadingBar();

  try {
    const response = await secureFetch(`${config.API_URL}spatial-search/pcdraw/export`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ project_id: ctx.projectId })
    });

    const payload = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(payload?.message || `Export failed with status ${response.status}`);
    }

    if (!payload?.url) {
      throw new Error('Export response did not include a download URL');
    }

    window.location.assign(payload.url);
  } catch (error) {
    console.error('Area selection export failed:', error);
    alert(error.message || translate('export.no_exportable_data'));
  } finally {
    hideLoadingBar();
    downloadBtn.disabled = false;
  }
}

async function deletePCDrawSelection(type, seq, featureId, rowIndex, options = {}) {
  const key = normalizePCType(type);
  const targetSeq = Number(seq);
  const previousSelections = [...(pcDrawSelectionsByType.get(key) || [])];
  const selectionIndex = previousSelections.findIndex(selection => Number(selection.seq) === targetSeq);

  if (!key || !Number.isFinite(targetSeq) || selectionIndex < 0) return;

  const targetSelection = previousSelections[selectionIndex];
  const properties = getPCSelectionPropertiesPayload(targetSelection);
  const targetFeatureId = String(featureId || '');
  const targetRowIndex = Number(rowIndex);
  let propertyIndex = Number.isInteger(targetRowIndex) && targetRowIndex >= 0
    ? targetRowIndex
    : -1;

  // The row index is the most precise identifier. Fall back to the feature id
  // for selections loaded from older payloads or markup.
  if (
    propertyIndex < 0 || propertyIndex >= properties.length
    || (targetFeatureId && getPCSelectionPropertyId(properties[propertyIndex]) !== targetFeatureId)
  ) {
    propertyIndex = properties.findIndex(property => getPCSelectionPropertyId(property) === targetFeatureId);
  }

  if (propertyIndex < 0 || propertyIndex >= properties.length) return;

  const remainingProperties = properties.filter((_, index) => index !== propertyIndex);
  const nextSelections = [...previousSelections];

  if (remainingProperties.length) {
    nextSelections[selectionIndex] = {
      ...targetSelection,
      count: remainingProperties.length,
      properties: JSON.stringify(remainingProperties)
    };
  } else {
    nextSelections.splice(selectionIndex, 1);
  }

  if (nextSelections.length) {
    pcDrawSelectionsByType.set(key, nextSelections);
  } else {
    pcDrawSelectionsByType.delete(key);
  }

  try {
    await persistPCDrawSelections(key);
    renderPCSummaryPanel();
    highlightLoadedPCSelections(getPCDrawSelections(), { fitToResults: false });
    // Keep the remaining enriched row properties (postcode/place) in memory.
    // Reloading here replaces them with the backend's reduced persistence payload.
    if (!options.silent) setStatus('Area Selection row deleted');
  } catch (error) {
    console.error('PC draw delete failed:', error);
    pcDrawSelectionsByType.set(key, previousSelections);
    renderPCSummaryPanel();
    highlightLoadedPCSelections(getPCDrawSelections(), { fitToResults: false });
    setStatus('Area Selection delete failed. Please try again.');
  }
}

function closePCSummaryPanel() {
  pcSummaryExplicitlyOpened = false;
  pcSummaryDeferredOpenToken++;
  setDockVisible('pc-summary', false);
  pcSummaryMinimized = false;
  pcSummaryActiveTab = 'details';
  pcSummaryStoredGeometry = null;
  if (pcSummaryDockButton) {
    pcSummaryDockButton.remove();
    pcSummaryDockButton = null;
  }
  if (pcSummaryPanel) {
    pcSummaryPanel.remove();
    pcSummaryPanel = null;
  }
}

function capturePCSummaryGeometry() {
  if (!pcSummaryPanel) return null;

  return {
    left: pcSummaryPanel.style.left || '',
    top: pcSummaryPanel.style.top || '',
    right: pcSummaryPanel.style.right || '',
    bottom: pcSummaryPanel.style.bottom || ''
  };
}

function applyPCSummaryStoredGeometry(geometry) {
  if (!pcSummaryPanel || !geometry) return;

  if (geometry.left) pcSummaryPanel.style.setProperty('left', geometry.left, 'important');
  else pcSummaryPanel.style.removeProperty('left');
  if (geometry.top) pcSummaryPanel.style.setProperty('top', geometry.top, 'important');
  else pcSummaryPanel.style.removeProperty('top');
  if (geometry.right) pcSummaryPanel.style.setProperty('right', geometry.right, 'important');
  else pcSummaryPanel.style.removeProperty('right');
  if (geometry.bottom) pcSummaryPanel.style.setProperty('bottom', geometry.bottom, 'important');
  else pcSummaryPanel.style.removeProperty('bottom');
  pcSummaryPanel.style.removeProperty('width');
  pcSummaryPanel.style.removeProperty('height');
}

function showPCSummaryPanel() {
  if (!pcSummaryPanel) return;
  pcSummaryPanel.style.display = 'block';
  pcSummaryPanel.classList.remove('is-minimized');
  if (pcSummaryStoredGeometry) {
    applyPCSummaryStoredGeometry(pcSummaryStoredGeometry);
  }
  setDockVisible('pc-summary', false);
  pcSummaryMinimized = false;
  requestAnimationFrame(() => placeFloatingDialog(pcSummaryPanel));
}

function minimizePCSummaryPanel() {
  if (!pcSummaryPanel) return;
  pcSummaryExplicitlyOpened = false;
  pcSummaryDeferredOpenToken++;
  pcSummaryStoredGeometry = capturePCSummaryGeometry();
  pcSummaryMinimized = true;
  pcSummaryPanel.classList.add('is-minimized');
  pcSummaryPanel.style.display = 'none';
  setDockVisible('pc-summary', true);
}

function getPCSummaryAreaId(item) {
  if (item?.summaryProperty) return getPCSelectionPropertyId(item.summaryProperty);
  return '';
}

function normalizePCSummaryKey(key) {
  return String(key || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function getPCSummaryPropertyValue(propertySources, candidates = [], patterns = []) {
  const entries = propertySources
    .filter(source => source && typeof source === 'object')
    .flatMap(source => Object.entries(source));
  const hasValue = value => value !== undefined && value !== null && String(value).trim() !== '';

  const normalizedCandidates = candidates.map(normalizePCSummaryKey);
  for (const candidate of normalizedCandidates) {
    const exactMatch = entries.find(([key, value]) =>
      normalizePCSummaryKey(key) === candidate && hasValue(value)
    );
    if (exactMatch) return exactMatch[1];
  }

  const patternMatch = entries.find(([key, value]) => {
    const normalizedKey = normalizePCSummaryKey(key);
    return hasValue(value) && patterns.some(pattern => pattern.test(normalizedKey));
  });

  return patternMatch?.[1] ?? '';
}

function getPCSummaryLevel(item) {
  return normalizePCType(item?.type).toUpperCase();
}

function getPCSummaryPostcodeCandidates(type) {
  const level = normalizePCType(type);
  const commonCandidates = [
    'postcode',
    'post_code',
    'postalcode',
    'postal_code',
    'postcode6_BAG',
    'u104723postcode6_BAG',
    'u107260postcode',
    'u108213Admin_naam',
    'u108213Admin_name',
    'Admin_naam',
    'Admin_name',
    'admin_naam',
    'admin_name',
    'name'
  ];

  if (level === 'pc6') {
    return [...(!isBelgiumPCSummary() ? ['6pp'] : []), '4pp', 'nouveau_po', 'bd_code', 'pc6', 'u104986pc6_cbs', ...commonCandidates, 'pc5', 'pc4', 'u104987pc4_cbs'];
  }

  if (level === 'nis6') {
    return ['nis6', 'cd_sub_munty', 'pc5', 'nis9', 'nis_code', 'niscode', ...commonCandidates, 'pc6', 'u104986pc6_cbs', 'pc4', 'u104987pc4_cbs'];
  }

  if (level === 'nis9') {
    return ['niscode', 'nis9', ...commonCandidates, 'id'];
  }

  if (level === 'pc4') {
    return ['4pp', 'pc4', 'u104987pc4_cbs', ...commonCandidates, 'pc5', 'pc6', 'u104986pc6_cbs'];
  }

  return ['nouveau_po', 'bd_code', ...commonCandidates, 'pc6', 'u104986pc6_cbs', 'pc5', 'pc4', 'u104987pc4_cbs'];
}

function normalizePCSummaryValue(value) {
  return String(value ?? '').trim().toLowerCase();
}

function isPCSummaryPostcodeLike(value, id, postcode) {
  const normalizedValue = normalizePCSummaryValue(value);
  if (!normalizedValue) return false;
  return normalizedValue === normalizePCSummaryValue(id)
    || normalizedValue === normalizePCSummaryValue(postcode)
    || /^\d{4,6}\s*[a-z]{0,2}$/i.test(normalizedValue);
}

function normalizePostcodeValue(value) {
  return String(value ?? '').trim().replace(/\s+/g, '').toUpperCase();
}

function getPostcodeMatchFields(level) {
  const normalizedLevel = String(level || '').toUpperCase();
  const common = ['postcode', 'postal_code', 'postalcode', 'postalCode', 'u107260postcode'];
  // u101365ViewGroupVboType1 exposes postcode4 through these two aliases.
  if (normalizedLevel === 'PC4') return ['4pp', 'u104987pc4_cbs', 'pc4', 'postcode4', 'u108248Admin_code', 'u108213Admin_naam', ...common];
  if (normalizedLevel === 'PC6') {
    return isBelgiumPCSummary()
      ? ['4pp', 'nouveau_po', 'bd_code', ...common]
      : ['6pp', 'u104986pc6_cbs', 'pc6', 'nl_6pp', 'postcode6_BAG', 'u104723postcode6_BAG', ...common];
  }
  if (normalizedLevel === 'NIS6') return ['nis6', 'cd_sub_munty', 'nis_code', 'niscode'];
  return [];
}

function featureMatchesPostcode(feature, level, postcodeSet) {
  return getMatchedPostcode(feature, level, postcodeSet) !== '';
}

function getMatchedPostcode(feature, level, postcodeSet) {
  const properties = feature?.getProperties?.() || {};
  // Feature IDs, address counts and display names are not postcode values.
  return getPostcodeMatchFields(level)
    .map(key => normalizePostcodeValue(properties[key]))
    .find(value => value && postcodeSet.has(value)) || '';
}

function getPCFeaturesForLevel(level) {
  const features = [];
  const seenSources = new Set();
  getSelectableLayers(level).forEach(layer => {
    [layer.get?.('wfsSource'), layer.getSource?.()].forEach(source => {
      if (!source || seenSources.has(source)) return;
      seenSources.add(source);
      features.push(...(source.getFeatures?.() || []));
    });
  });
  return features;
}

async function loadPCFeaturesForVisibleExtent(level, postcodeSet) {
  const currentMap = map || getMap();
  if (!currentMap?.getView) return [];
  const view = currentMap.getView();
  const size = currentMap.getSize();
  if (!size) return [];

  const extent = view.calculateExtent(size);
  const resolution = view.getResolution();
  const projection = view.getProjection();

  for (let attempt = 0; attempt < 8; attempt += 1) {
    const seenSources = new Set();
    getSelectableLayers(level).forEach(layer => {
      [layer.get?.('wfsSource'), layer.getSource?.()].forEach(source => {
        if (!source || seenSources.has(source)) return;
        seenSources.add(source);
        source.loadFeatures?.(extent, resolution, projection);
      });
    });

    await waitForPCFeatureLoadAttempt(attempt < 2 ? 250 : 450);
    const matches = getPCFeaturesForLevel(level)
      .filter(feature => featureMatchesPostcode(feature, level, postcodeSet));
    if (new Set(matches.map(feature => getMatchedPostcode(feature, level, postcodeSet))).size >= postcodeSet.size) return matches;
  }

  return getPCFeaturesForLevel(level)
    .filter(feature => featureMatchesPostcode(feature, level, postcodeSet));
}

async function loadPCFeaturesByPostcode(level, postcodes) {
  let lastError = null;
  let successfulRequests = 0;
  const fields = getPostcodeMatchFields(level);
  for (const layer of getSelectableLayers(level)) {
    const source = layer.get?.('wfsSource') || layer.getSource?.();
    const typeName = layer.get?.('typeName');
    if (!source || !typeName) continue;
    const normalizedType = String(level || '').toLowerCase();
    if (!isBelgiumPCSummary() && ['pc4', 'pc6'].includes(normalizedType)) {
      // Use the existing authenticated geometry endpoint. Direct CQL requests
      // to GeoServer can be rejected before CORS headers are added.
      for (let offset = 0; offset < postcodes.length; offset += 500) {
        const params = new URLSearchParams({ type: normalizedType });
        postcodes.slice(offset, offset + 500).forEach(code => params.append('ids[]', normalizePostcodeValue(code)));
        const response = await secureFetch(`${config.API_URL}spatial-search/pc-features?${params.toString()}`);
        if (!response.ok) {
          throw new Error(`Netherlands postcode geometry lookup failed (HTTP ${response.status})`);
        }
        const geojson = await response.json();
        if (geojson?.type !== 'FeatureCollection' || !Array.isArray(geojson.features)) {
          throw new Error('Netherlands postcode geometry lookup returned invalid GeoJSON');
        }
        const features = new GeoJSON({ geometryName: '__mapGeometry' }).readFeatures(geojson, {
          dataProjection: 'EPSG:3857', featureProjection: 'EPSG:3857'
        });
        features.forEach(feature => {
          const id = getPCFeatureIdentifier(feature);
          if (id) feature.setId(id);
          const code = getMatchedPostcode(feature, level, new Set(postcodes.map(normalizePostcodeValue)));
          const existing = (id && source.getFeatureById?.(id)) || source.getFeatures?.().find(candidate =>
            code && getMatchedPostcode(candidate, level, new Set([code])) === code);
          if (existing) {
            // Keep the cached polygon but refresh its descriptive attributes.
            const geometryName = feature.getGeometryName();
            const attributes = Object.fromEntries(Object.entries(feature.getProperties()).filter(([key]) =>
              key !== geometryName && key !== 'geometry'));
            existing.setProperties(attributes);
            if (!existing.getGeometry?.() && feature.getGeometry()) existing.setGeometry(feature.getGeometry());
          } else source.addFeature(feature);
        });
        successfulRequests++;
      }
      continue;
    }
    const isBelgiumPostcode = String(typeName).toLowerCase() === 'geolinq:be_4pp';
    const isPublicPostcode = isBelgiumPostcode
      || ['geolinq:nl_pc6', 'geolinq:nl_4pp', 'geolinq:nl_6pp'].includes(String(typeName).toLowerCase());
    const sampleProperties = source.getFeatures?.()[0]?.getProperties?.() || {};
    const lookupFields = isBelgiumPostcode ? ['postcode', '4pp'] : fields;
    const knownFields = lookupFields.filter(field => Object.hasOwn(sampleProperties, field));
    for (const field of knownFields.length ? knownFields : lookupFields) {
      const numericField = typeof sampleProperties[field] === 'number';
      const values = postcodes.map(value => numericField && /^\d+$/.test(String(value))
        ? String(Number(value))
        : `'${String(value).replace(/'/g, "''")}'`
      );
      // Current public GeoServer layers use WFS, like the map source itself.
      // Keep the legacy authenticated feature endpoint for older layer schemas.
      const params = new URLSearchParams({
        layerName: typeName,
        srsName: 'EPSG:3857',
        cqlFilter: values.map(value => `"${field}" = ${value}`).join(' OR ')
      });
      try {
        let response;
        if (isPublicPostcode) {
          const wfsParams = new URLSearchParams({
            service: 'WFS', version: '1.0.0', request: 'GetFeature',
            typeName, outputFormat: 'application/json', srsName: 'EPSG:3857',
            CQL_FILTER: params.get('cqlFilter')
          });
          response = await fetch(`${config.GEOSERVER_URL}?${wfsParams.toString()}`, {
            headers: { Accept: 'application/json' }
          });
        } else {
          response = await secureFetch(`${config.API_URL}feature-info/get-features?${params.toString()}`);
        }
        if (!response.ok) {
          const error = new Error(response.status === 403
            ? 'GeoServer rejected the postcode lookup (HTTP 403). The server configuration must allow this request.'
            : `Postcode feature lookup failed (HTTP ${response.status})`);
          error.stopFieldRetries = response.status === 401 || response.status === 403;
          throw error;
        }
        const body = await response.text();
        if (body.trimStart().startsWith('<')) {
          throw new Error('Postcode feature lookup returned XML or HTML instead of GeoJSON');
        }
        const geojson = JSON.parse(body);
        if (geojson?.type !== 'FeatureCollection' || !Array.isArray(geojson.features)) {
          throw new Error('Postcode feature lookup returned an invalid GeoJSON feature collection');
        }
        const features = new GeoJSON({ geometryName: '__mapGeometry' }).readFeatures(geojson, { dataProjection: 'EPSG:3857', featureProjection: 'EPSG:3857' });
        successfulRequests++;
        features.forEach(feature => { const id = getPCFeatureIdentifier(feature); if (id) feature.setId(id); if (!id || !source.getFeatureById?.(id)) source.addFeature(feature); });
        if (features.length) break;
      } catch (error) {
        if (error.stopFieldRetries || error instanceof TypeError) throw error;
        lastError = error;
      }
    }
  }
  if (!successfulRequests && lastError) throw lastError;
}

export async function selectPCFeaturesByPostcodes(level, postcodes, options = {}) {
  if (!map) map = getMap();
  const requestedLevel = String(level || '').trim().toUpperCase();
  // Belgium has a layer named PostalCode, not PC6. Reuse the established
  // selection storage key internally while resolving features exclusively
  // from the PostalCode layer identity.
  const normalizedLevel = requestedLevel === 'POSTALCODE' && isBelgiumPCSummary()
    ? 'PC6'
    : requestedLevel;
  const values = [...new Set((postcodes || []).map(normalizePostcodeValue).filter(Boolean))];
  if (!map || !PC_SELECTION_LEVELS.includes(normalizedLevel) || !values.length) return [];
  const preservedSelections = options.clearExisting === false
    ? getPCDrawSelections().map(selection => ({ ...selection }))
    : [];
  pcSelectedAreaVisibility.set(normalizedLevel, true);
  await waitForPCSelectableLayers([{ type: normalizedLevel }], {
    maxAttempts: 16,
    delayMs: 250
  });
  const postcodeSet = new Set(values);
  let matches = getPCFeaturesForLevel(normalizedLevel).filter(feature => featureMatchesPostcode(feature, normalizedLevel, postcodeSet));
  const hasAllPostcodes = features => new Set(features.map(feature => getMatchedPostcode(feature, normalizedLevel, postcodeSet))).size === values.length;
  if (!hasAllPostcodes(matches)) {
    matches = await loadPCFeaturesForVisibleExtent(normalizedLevel, postcodeSet);
  }
  const needsSummaryMetadata = !isBelgiumPCSummary() && matches.some(feature => {
    const props = feature.getProperties?.() || {};
    const place = normalizedLevel === 'PC4' ? props.woonplaats ?? props.city ?? props.place
      : props.gemeente ?? props.city ?? props.place;
    const count = normalizedLevel === 'PC4' ? props.address_count_sum ?? props.addressCount ?? props.u108212Aantal_VBO_BAG
      : props.total_hh ?? props.addressCount;
    return place == null || String(place).trim() === '' || count == null || String(count).trim() === '';
  });
  if (!hasAllPostcodes(matches) || needsSummaryMetadata) {
    try {
      await loadPCFeaturesByPostcode(normalizedLevel, values);
    } catch (error) {
      // A failed lookup must not discard polygons already loaded on the map.
      matches = getPCFeaturesForLevel(normalizedLevel)
        .filter(feature => featureMatchesPostcode(feature, normalizedLevel, postcodeSet));
      if (!matches.length) {
        throw new Error(`Cannot highlight postcodes ${values.join(', ')}: polygon geometry is not loaded. ${error.message}`);
      }
      console.warn('Some postcode polygons could not be fetched; highlighting loaded matches.', error);
    }
    matches = getPCFeaturesForLevel(normalizedLevel)
      .filter(feature => featureMatchesPostcode(feature, normalizedLevel, postcodeSet));
  }
  let uniqueMatches = [...new Map(matches.map(feature => [getPCFeatureIdentifier(feature) || JSON.stringify(feature.getProperties()), feature])).values()];
  if (!uniqueMatches.length) return [];
  const matchedFeatures = [...uniqueMatches];
  const matchedCodes = new Set(uniqueMatches.map(feature => getMatchedPostcode(feature, normalizedLevel, postcodeSet)));
  const missingCodes = values.filter(value => !matchedCodes.has(value));
  if (options.clearExisting !== false) {
    pcDrawSelectionsByType.clear();
    pcSummaryPage = 1;
  } else {
    // Layer/source loading can refresh selection state while this asynchronous
    // lookup is running. Merge the snapshot back before appending the new row.
    preservedSelections.forEach(selection => {
      const type = getPCSelectionType(selection);
      const existing = getSelectionByTypeAndSeq(type, selection.seq);
      if (!existing) upsertPCDrawSelection(type, selection);
    });
  }
  uniqueMatches = getUnselectedPCFeatures(normalizedLevel, uniqueMatches);

  const resultRowsByPostcode = new Map(
    (Array.isArray(options.resultRows) ? options.resultRows : []).map(row => {
      const postcode = row?.id ?? row?.postcode ?? row?.postal_code ?? row?.code;
      return [normalizePostcodeValue(postcode), row];
    }).filter(([postcode]) => postcode)
  );
  const enrichedProperties = matchedFeatures.map(feature => {
    const id = getPCFeatureIdentifier(feature);
    if (!id) return null;
    const matchedPostcode = getMatchedPostcode(feature, normalizedLevel, postcodeSet);
    const resultRow = resultRowsByPostcode.get(matchedPostcode);
    const geometryName = feature.getGeometryName?.() || 'geometry';
    const scalarProperties = Object.fromEntries(Object.entries(feature.getProperties?.() || {}).filter(([key, value]) =>
      key !== 'geometry' && key !== geometryName
      && (value === null || ['string', 'number', 'boolean'].includes(typeof value))
    ));
    const details = getPCSummaryDetails({ type: normalizedLevel, summaryProperty: { ...scalarProperties, id } });
    return {
      ...scalarProperties,
      ...resultRow,
      id,
      postcode: resultRow?.postcode ?? resultRow?.postal_code ?? resultRow?.code ?? matchedPostcode ?? id,
      place: details.place || resultRow?.place || resultRow?.city || resultRow?.locality || resultRow?.name || '',
      addressCount: details.addressCount !== '' ? details.addressCount
        : (resultRow?.addressCount ?? resultRow?.address_count ?? resultRow?.addresses ?? resultRow?.count ?? getPCFeatureAddressCount(feature))
    };
  }).filter(Boolean);
  const enrichedByCode = new Map(enrichedProperties.map(property => [normalizePostcodeValue(property.postcode), property]));
  getPCDrawSelections().filter(selection => normalizePCType(selection.type) === normalizePCType(normalizedLevel)).forEach(selection => {
    const properties = getPCSelectionPropertiesPayload(selection).map(property => {
      const code = normalizePostcodeValue(getPCSelectionLookupId(property, normalizedLevel));
      const enriched = enrichedByCode.get(code);
      return enriched ? { ...property, ...enriched } : property;
    });
    upsertPCDrawSelection(normalizedLevel, { ...selection, properties: JSON.stringify(properties) });
  });
  if (!uniqueMatches.length) {
    highlightLoadedPCSelections(getPCDrawSelections(), { fitToResults: options.fitToResults !== false });
    renderPCSummaryPanel();
    showPCSummaryPanel();
    await persistPCDrawSelections(normalizedLevel);
    if (missingCodes.length) setStatus(`PostalCode polygons not loaded: ${missingCodes.join(', ')}`);
    return matchedFeatures;
  }
  const newIds = new Set(uniqueMatches.map(getPCFeatureIdentifier));
  const properties = enrichedProperties.filter(property => newIds.has(property.id));
  if (!properties.length) return [];
  upsertPCDrawSelection(normalizedLevel, {
    seq: allocatePCDrawSeq(),
    count: properties.length,
    addressCount: getSelectedFeaturesAddressCount(uniqueMatches),
    properties: JSON.stringify(properties)
  });
  const highlightedFeatures = highlightLoadedPCSelections(getPCDrawSelections(), {
    fitToResults: options.fitToResults !== false
  });
  renderPCSummaryPanel();
  showPCSummaryPanel();
  await persistPCDrawSelections(normalizedLevel);
  sendPCAreaSnapshot(highlightedFeatures);
  if (missingCodes.length) setStatus(`Highlighted ${matchedCodes.size} postcode(s). Polygons not loaded: ${missingCodes.join(', ')}`);
  return matchedFeatures;
}

function getPCSummaryPlaceValue(sources, id, postcode) {
  const cityValue = getPCSummaryPropertyValue(
    sources,
    ['sector_name', 'city', 'place', 'u104724woonplaats', 'u107262postname_nl', 'postname_nl', 'stad', 'woonplaats', 'locality'],
    [/^sectorname$/, /^city$/, /^place$/, /u104724woonplaats/, /u107262postnamenl/, /postnamenl/, /stad/, /woonplaats/, /locality/]
  );

  if (cityValue && !isPCSummaryPostcodeLike(cityValue, id, postcode)) {
    return cityValue;
  }

  const fallbackValue = getPCSummaryPropertyValue(
    sources,
    ['municipality', 'gemeente_naam', 'gemeente'],
    [/municipality/, /gemeente/]
  );

  return isPCSummaryPostcodeLike(fallbackValue, id, postcode) ? '' : fallbackValue;
}

function getBelgiumPC5AdminCandidates() {
  const option = document.getElementById('language-select')?.selectedOptions?.[0];
  const language = [
    option?.dataset?.abbreviation,
    option?.dataset?.name,
    option?.textContent
  ].filter(Boolean).join(' ').toLowerCase();

  let selectedField = 'u108267Admin_NL';
  if (/\b(fr|french|fran[cç]ais)\b/.test(language)) {
    selectedField = 'u108266Admin_FR';
  } else if (/\b(de|ger|german|deutsch)\b/.test(language)) {
    selectedField = 'u108265Admin_GER';
  } else if (/\b(nl|dutch|nederlands)\b/.test(language)) {
    selectedField = 'u108267Admin_NL';
  }

  return [
    selectedField,
    ...['u108267Admin_NL', 'u108266Admin_FR', 'u108265Admin_GER']
      .filter(field => field !== selectedField)
  ];
}

function getBelgiumNIS6PlaceCandidates() {
  const option = document.getElementById('language-select')?.selectedOptions?.[0];
  const language = [
    option?.dataset?.abbreviation,
    option?.dataset?.name,
    option?.textContent
  ].filter(Boolean).join(' ').toLowerCase();

  // The NIS6 source currently provides Dutch and French names. English and
  // any language without a dedicated NIS6 name use the Dutch name.
  const selectedField = /\b(fr|french|fran[cÃ§]ais)\b/.test(language)
    ? 'nis6_name_fr'
    : 'nis6_name_nl';

  return [
    selectedField,
    ...['nis6_name_nl', 'nis6_name_fr'].filter(field => field !== selectedField)
  ];
}

function getPCSummaryFeature(item) {
  const type = normalizePCType(item?.type);
  const featureId = getPCSummaryAreaId(item);
  if (!type || !featureId) return null;

  const selectedPrefix = `${type}:${Number(item.seq) || 0}:`;
  const selectedFeature = Array.from(selectedFeaturesMap.values()).find(feature => {
    const id = String(feature?.getId?.() || '');
    const areaType = normalizePCType(feature?.get?.('_pcAreaType'));
    if (id === `${selectedPrefix}${featureId}`) return true;
    return id.startsWith(selectedPrefix)
      && areaType === type
      && getPCFeatureIdentifier(feature) === String(featureId);
  });

  return selectedFeature || findPCFeatureById(type, featureId);
}

function getPCSummaryDetails(item) {
  const property = item?.summaryProperty || getPCSelectionPropertiesPayload(item)[0] || {};
  const feature = getPCSummaryFeature(item);
  const featureProperties = feature?.getProperties?.() || {};
  const sources = [property, featureProperties, item];
  const id = getPCSummaryAreaId(item);
  let postcode = getPCSummaryPropertyValue(
    sources,
    getPCSummaryPostcodeCandidates(item?.type),
    [/postcode/, /postalcode/, /^pc[456]$/]
  ) || '';
  const rawPlaceValue = getPCSummaryPropertyValue(
    sources,
    ['city', 'place', 'u104724woonplaats', 'u107262postname_nl', 'postname_nl', 'stad', 'woonplaats', 'locality'],
    [/^city$/, /^place$/, /u104724woonplaats/, /u107262postnamenl/, /postnamenl/, /stad/, /woonplaats/, /locality/]
  );
  let placeValue = getPCSummaryPlaceValue(sources, id, postcode);
  if (normalizePCType(item?.type) === 'nis9') {
    placeValue = getPCSummaryPropertyValue(sources, ['gemeente']) || placeValue;
  }

  if (normalizePCType(item?.type) === 'pc5' && getSelectedCountryAbbreviation?.() === BELGIUM_COUNTRY_CODE) {
    const adminCandidates = getBelgiumPC5AdminCandidates();
    const adminLabel = getPCSummaryPropertyValue(
      sources,
      adminCandidates,
      [/u108267adminnl/, /u108266adminfr/, /u108265adminger/]
    );
    if (adminLabel) postcode = adminLabel;
  }

  // Some restored PC features expose their postcode through a generic name/place
  // attribute. Keep that value in the PostCode column, including Dutch "1234 AB"
  // formatting, but never treat the internal source id as a postcode.
  if (
    !postcode
    && normalizePCSummaryValue(rawPlaceValue) !== normalizePCSummaryValue(id)
    && /^\d{4,6}\s*[a-z]{0,2}$/i.test(String(rawPlaceValue || '').trim())
  ) {
    postcode = rawPlaceValue;
    placeValue = '';
  }
  const isBelgium = isBelgiumPCSummary();
  const isPC4 = normalizePCType(item?.type) === 'pc4';
  const isNIS6 = normalizePCType(item?.type) === 'nis6';
  const isPC6 = normalizePCType(item?.type) === 'pc6';
  if (!isBelgium && isPC4) {
    placeValue = getPCSummaryPropertyValue(sources, ['woonplaats']) || placeValue;
  }
  if (!isBelgium && isPC6) {
    placeValue = getPCSummaryPropertyValue(sources, ['gemeente']) || placeValue;
  }
  if (isBelgium && isNIS6) {
    placeValue = getPCSummaryPropertyValue(
      sources,
      getBelgiumNIS6PlaceCandidates(),
      [/^nis6namenl$/, /^nis6namefr$/]
    ) || placeValue;
  }
  const addressValue = getPCSummaryPropertyValue(
    sources,
    isBelgium
      ? (isPC6 ? ['aantaladressen', 'addressCount', 'address_count', 'addresses'] : ['total_mailbox'])
      : [...(isPC4 ? ['address_count_sum'] : []), ...(isPC6 ? ['total_hh'] : []), 'u108212Aantal_VBO_BAG', 'u108264Aantal_adres', 'u108268Aantal_adres_bepost', 'number_of_addresses', 'addresses', 'addressCount', 'address_count', 'aantal_adressen', 'aantal'],
    isBelgium
      ? (isPC6 ? [/^aantaladressen$/] : [/^totalmailbox$/])
      : [/addresscount/, /addresses/, /aantal.*adres/, /aantalvbobag/, /u108212aantalvbobag/, /u108264aantaladres/, /u108268aantaladresbepost/]
  );
  const addressCount = isBelgium
    ? (isNIS6 || isPC6 ? (Number(addressValue) || 0) : '')
    : (Number(addressValue !== '' ? addressValue : (getPCFeatureAddressCount(feature) || item.addressCount || 0)) || 0);
  const getPC4DistributionValue = (candidates, patterns) => isPC4
    ? getPCSummaryPropertyValue(sources, candidates, patterns)
    : '';
  const getNIS6DistributionValue = (candidates, patterns) => isNIS6
    ? getPCSummaryPropertyValue(sources, candidates, patterns)
    : '';
  const mailboxYesPub = getPCSummaryPropertyValue(sources, ['total_mailbox_yespub'], [/^totalmailboxyespub$/]);
  const mailboxNoPub = getPCSummaryPropertyValue(sources, ['total_mailbox_nopub'], [/^totalmailboxnopub$/]);
  const hasMailboxNoPub = mailboxNoPub != null && String(mailboxNoPub).trim() !== '';
  const numericAddresses = Number(String(addressCount ?? '').replace(',', '.'));
  const numericNoPub = Number(String(mailboxNoPub ?? '').replace(',', '.'));
  const noPubPercentage = hasMailboxNoPub
    && Number.isFinite(numericAddresses)
    && numericAddresses > 0
    && Number.isFinite(numericNoPub)
      ? ((numericNoPub / numericAddresses) * 100).toFixed(2)
      : '';

  return {
    id,
    postcode,
    place: placeValue,
    addressCount,
    brutoVsp: getNIS6DistributionValue(['total_mailbox'], [/^totalmailbox$/])
      || getPC4DistributionValue(['bruto_vsp', 'brutoVsp'], [/^brutovsp$/]),
    nettoVsp: getNIS6DistributionValue(['total_mailbox_yespub'], [/^totalmailboxyespub$/])
      || getPC4DistributionValue(['netto_vsp', 'nettoVsp'], [/^nettovsp$/]),
    aantalWeekblad: getPC4DistributionValue(['aantal_weekblad', 'aantalWeekblad'], [/^aantalweekblad$/]),
    brutoWeekblad: getPC4DistributionValue(['bruto_weekblad', 'brutoWeekblad'], [/^brutoweekblad$/]),
    mailboxYesPub,
    mailboxNoPub,
    noPubPercentage,
    bdBruto: getPCSummaryPropertyValue(sources, ['box_brut_bd'], [/^boxbrutbd$/]),
    bdNetto: getPCSummaryPropertyValue(sources, ['box_net_bd'], [/^boxnetbd$/])
  };
}

function getPCSummaryRows() {
  const seen = new Set();
  const selectionRows = getPCDrawSelections().flatMap(selection => {
    const properties = getPCSelectionPropertiesPayload(selection)
      .map((property, index) => ({ property, index }))
      .filter(({ property }) => getPCSelectionPropertyId(property));

    return properties.filter(({ property }) => {
      const key = `${normalizePCType(selection.type)}:${getPCSelectionLookupId(property, selection.type)}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }).map(({ property, index }) => ({
      ...selection,
      summaryProperty: property,
      summaryFeatureId: getPCSelectionPropertyId(property),
      summaryRowIndex: index
    }));
  });
  return [...selectionRows, ...areaSearchSummaryRows];
}

export function showPostcodeResultsInAreaSummary(rows, selectedPlace = {}) {
  const defaultPlace = selectedPlace.display_name ?? selectedPlace.label ?? selectedPlace.name ?? '';
  areaSearchSummaryRows = (Array.isArray(rows) ? rows : []).map((row, index) => {
    const postcode = row?.postal_code ?? row?.postcode ?? row?.post_code ?? row?.postalCode ?? row?.code ?? '';
    const place = row?.place ?? row?.city ?? row?.locality ?? row?.name ?? defaultPlace;
    const addressCount = row?.address_count ?? row?.addressCount ?? row?.addresses ?? row?.count ?? 0;
    const id = row?.id ?? `${selectedPlace.id ?? defaultPlace}:${postcode || index}`;
    return {
      type: 'area-search',
      seq: index + 1,
      externalAreaSearch: true,
      summaryRowIndex: index,
      summaryFeatureId: String(id),
      summaryProperty: { ...row, id, postcode, place, addressCount }
    };
  });
  pcSummaryPage = 1;
  pcSummaryMinimized = false;
  renderPCSummaryPanel();
  showPCSummaryPanel();
}

function arePCSummaryRowsEnriched() {
  const rows = getPCSummaryRows();
  return rows.length > 0 && rows.every(row => String(getPCSummaryDetails(row).postcode || '').trim());
}

function schedulePCSummaryOpenWhenReady(savedSelections, options = {}) {
  const token = ++pcSummaryDeferredOpenToken;

  window.setTimeout(async () => {
    for (let attempt = 0; attempt < 80 && token === pcSummaryDeferredOpenToken; attempt += 1) {
      if (attempt === 0 || attempt % 10 === 0) {
        await loadPCSelectionFeaturesById(savedSelections);
      }
      if (arePCSummaryRowsEnriched()) {
        renderPCSummaryPanel();
        if (options.openPanel === true) showPCSummaryPanel();
        sendPCAreaSnapshot();
        return;
      }

      triggerPCLayerLoadForResults(savedSelections, { fullExtent: attempt > 10 });
      await waitForPCFeatureLoadAttempt(300);
      highlightLoadedPCSelections(savedSelections, {
        fitToResults: false,
        emitMessages: false
      });
    }

    // Avoid leaving the user's saved summary inaccessible if a particular WFS
    // feature cannot be enriched within the retry window.
    if (token === pcSummaryDeferredOpenToken && savedSelections.length) {
      renderPCSummaryPanel();
      if (options.openPanel === true) showPCSummaryPanel();
    }
  }, 0);
}

function getPCSummaryValue(item, key) {
  const details = getPCSummaryDetails(item);
  switch (key) {
    case 'id':
      return details.id;
    case 'level':
      return getPCSummaryLevel(item);
    case 'postcode':
      return details.postcode;
    case 'place':
      return details.place;
    case 'addressCount':
      return details.addressCount;
    case 'brutoVsp':
      return details.brutoVsp;
    case 'nettoVsp':
      return details.nettoVsp;
    case 'aantalWeekblad':
      return details.aantalWeekblad;
    case 'brutoWeekblad':
      return details.brutoWeekblad;
    case 'mailboxYesPub':
      return details.mailboxYesPub;
    case 'mailboxNoPub':
      return details.mailboxNoPub;
    case 'noPubPercentage':
      return details.noPubPercentage;
    case 'bdBruto':
      return details.bdBruto;
    case 'bdNetto':
      return details.bdNetto;
    default:
      return '';
  }
}

function filterPCSummaryRows(rows) {
  return rows.filter(item =>
    Object.entries(pcSummaryFilters).every(([key, filter]) => {
      const value = String(getPCSummaryValue(item, key)).toLowerCase();
      return !filter || value.includes(String(filter).toLowerCase());
    })
  );
}

function sortPCSummaryRows(rows) {
  const key = pcSummarySort?.key || 'postcode';
  const direction = pcSummarySort?.direction === 'desc' ? -1 : 1;

  return [...rows].sort((a, b) => {
    const left = getPCSummaryValue(a, key);
    const right = getPCSummaryValue(b, key);

    if (typeof left === 'number' && typeof right === 'number') {
      return (left - right) * direction;
    }

    return String(left).localeCompare(String(right), undefined, {
      numeric: true,
      sensitivity: 'base'
    }) * direction;
  });
}

function getPCSummarySortIcon(key) {
  if (pcSummarySort?.key !== key) return 'bi-arrow-down-up';
  return pcSummarySort.direction === 'desc' ? 'bi-sort-down' : 'bi-sort-up';
}

function getPCSummaryFilterIcon(key) {
  return pcSummaryFilters[key] ? 'bi-funnel-fill' : 'bi-funnel';
}

function getPCSummaryFilterLabel(key) {
  const configuredColumn = ALL_PC_SUMMARY_COLUMNS.find(column => column.key === key);
  if (configuredColumn) return configuredColumn.label;
  switch (key) {
    case 'level':
      return 'Level';
    case 'postcode':
      return 'PostCode';
    case 'place':
      return 'Place';
    case 'addressCount':
      return 'Addresses';
    case 'brutoVsp':
      return 'Bruto VSP';
    case 'nettoVsp':
      return 'Netto VSP';
    case 'aantalWeekblad':
      return 'Aantal Weekblad';
    case 'brutoWeekblad':
      return 'Bruto Weekblad';
    default:
      return 'Filter';
  }
}

function renderPCSummaryHeaderCell(key, label) {
  return `
    <div class="pc-summary-header-controls">
      <button class="pc-summary-sort-btn" data-sort-key="${key}" title="Sort ${label}" aria-label="Sort ${label}">
        <span>${label}</span><i class="bi ${getPCSummarySortIcon(key)}"></i>
      </button>
      <button class="pc-summary-filter-btn ${pcSummaryFilters[key] ? 'is-active' : ''}" data-filter-key="${key}" title="Filter ${label}" aria-label="Filter ${label}">
        <i class="bi ${getPCSummaryFilterIcon(key)}"></i>
      </button>
    </div>
  `;
}

function renderPCSummaryFilterDialog() {
  if (!pcSummaryFilterDialogKey) return '';

  const label = getPCSummaryFilterLabel(pcSummaryFilterDialogKey);
  return `
    <div class="pc-summary-filter-dialog" role="dialog" aria-modal="false" aria-label="Filter ${label}">
      <div class="pc-summary-filter-dialog-header">
        <b>Filter ${label}</b>
        <button type="button" class="pc-summary-filter-close btn-close btn-sm" aria-label="Close" title="Close"></button>
      </div>
      <input
        type="${ALL_PC_SUMMARY_COLUMNS.some(column => column.key === pcSummaryFilterDialogKey) ? 'number' : 'text'}"
        class="pc-summary-filter-dialog-input"
        value="${escapeHtml(pcSummaryFilters[pcSummaryFilterDialogKey])}"
        aria-label="Filter ${label}"
      />
      <div class="pc-summary-filter-dialog-actions">
        <button type="button" class="btn btn-sm btn-outline-secondary pc-summary-filter-clear">Clear</button>
        <button type="button" class="btn btn-sm btn-outline-secondary pc-summary-filter-cancel">Cancel</button>
        <button type="button" class="btn btn-sm sevenn-primary-button pc-summary-filter-apply">Apply</button>
      </div>
    </div>
  `;
}

function isPCSummaryColumnVisible(key) {
  return pcSummaryColumnVisibility[key] !== false;
}

function renderPCSummaryTotals(rows) {
  const columns = getPCSummaryColumns();
  const totals = Object.fromEntries(columns.map(column => [column.key, 0]));
  const populatedCounts = Object.fromEntries(columns.map(column => [column.key, 0]));
  rows.forEach(item => {
    columns.forEach(column => {
      const rawValue = getPCSummaryValue(item, column.key);
      const numericValue = Number(String(rawValue ?? '').replace(',', '.'));
      if (rawValue !== '' && rawValue != null && Number.isFinite(numericValue)) {
        totals[column.key] += numericValue;
        populatedCounts[column.key] += 1;
      }
    });
  });
  if (isBelgiumPCSummary()) {
    const visibleColumns = columns.filter(column => isPCSummaryColumnVisible(column.key));
    const groups = [...new Set(visibleColumns.map(column => column.group))];
    return `<div id="pc-summary-totals" class="pc-summary-totals" ${pcSummaryActiveTab === 'totals' ? '' : 'hidden'}>
      <table class="table table-sm table-bordered">
        <thead>
          <tr><th rowspan="2">Totals</th>${groups.map(group => `<th colspan="${visibleColumns.filter(column => column.group === group).length}">${group}</th>`).join('')}</tr>
          <tr>${visibleColumns.map(column => `<th>${column.label}</th>`).join('')}</tr>
        </thead>
        <tbody><tr><th scope="row">Selected areas</th>${visibleColumns.map(column => {
          const value = column.aggregate === 'ratio'
            ? (populatedCounts.mailboxNoPub > 0 && totals.addressCount > 0
              ? (totals.mailboxNoPub / totals.addressCount) * 100
              : null)
            : totals[column.key];
          const formatOptions = column.aggregate === 'ratio'
            ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
            : { maximumFractionDigits: 2 };
          return `<td>${value == null ? '' : value.toLocaleString(undefined, formatOptions)}</td>`;
        }).join('')}</tr></tbody>
      </table>
      <small>Totals across ${rows.length} selected area${rows.length === 1 ? '' : 's'}.</small>
    </div>`;
  }
  const groups = ['Overheid', 'PostNL', 'Kranten'];
  return `<div id="pc-summary-totals" class="pc-summary-totals" ${pcSummaryActiveTab === 'totals' ? '' : 'hidden'}>
    <table class="table table-sm">
      <thead><tr><th>Totals</th>${groups.map(group => `<th>${group}</th>`).join('')}</tr></thead>
      <tbody>${['bruto', 'netto'].map(slot => `<tr>
        <th scope="row">${slot === 'bruto' ? 'Bruto' : 'Netto'}</th>
        ${groups.map(group => {
          const column = NETHERLANDS_PC_SUMMARY_COLUMNS.find(candidate => candidate.group === group && candidate.slot === slot);
          return column
            ? `<td title="${column.label}">${totals[column.key].toLocaleString()}</td>`
            : '<td class="is-unavailable">&mdash;</td>';
        }).join('')}
      </tr>`).join('')}</tbody>
    </table>
    <small>Totals across ${rows.length} selected area${rows.length === 1 ? '' : 's'}.</small>
  </div>`;
}

function renderPCSummaryColumnSettings() {
  if (!pcSummaryColumnSettingsOpen) return '';
  if (isBelgiumPCSummary()) {
    const columns = getPCSummaryColumns();
    const groups = [...new Set(columns.map(column => column.group))];
    return `
      <div class="pc-summary-column-settings" role="dialog" aria-label="Select visible columns">
        <div class="pc-summary-column-settings-header">
          <b>Settings</b>
          <button type="button" class="pc-summary-column-settings-close btn-close btn-sm" aria-label="Close" title="Close"></button>
        </div>
        <table class="pc-summary-column-settings-table">
          <thead><tr><th>Distributors</th><th>Property</th><th>Show</th></tr></thead>
          <tbody>${groups.flatMap(group => columns.filter(column => column.group === group).map((column, index) => `<tr class="pc-summary-column-option">
            ${index === 0 ? `<th scope="row" rowspan="${columns.filter(candidate => candidate.group === group).length}">${group}</th>` : ''}
            <td>${column.label}</td>
            <td><input type="checkbox" aria-label="Show ${column.label}" data-column-key="${column.key}" ${isPCSummaryColumnVisible(column.key) ? 'checked' : ''}></td>
          </tr>`)).join('')}</tbody>
        </table>
      </div>`;
  }
  const groupedColumns = ['Overheid', 'PostNL', 'Kranten'].map(group => ({
    group,
    bruto: NETHERLANDS_PC_SUMMARY_COLUMNS.find(column => column.group === group && column.slot === 'bruto'),
    netto: NETHERLANDS_PC_SUMMARY_COLUMNS.find(column => column.group === group && column.slot === 'netto')
  }));
  return `
    <div class="pc-summary-column-settings" role="dialog" aria-label="Select visible columns">
      <div class="pc-summary-column-settings-header">
        <b>Distributors</b>
        <button type="button" class="pc-summary-column-settings-close btn-close btn-sm" aria-label="Close" title="Close"></button>
      </div>
      <table class="pc-summary-column-settings-table">
        <thead><tr><th>Distributor</th><th>Bruto</th><th>Netto</th></tr></thead>
        <tbody>${groupedColumns.map(({ group, bruto, netto }) => `<tr class="pc-summary-column-option distributor-${group.toLowerCase()}">
          <th scope="row">${group}</th>
          ${[bruto, netto].map(column => {
            if (!column) return '<td class="is-unavailable">—</td>';
            const checked = isPCSummaryColumnVisible(column.key);
            return `<td><input type="checkbox" aria-label="Show ${group} ${column.label}" title="${column.label}" data-column-key="${column.key}" ${checked ? 'checked' : ''}></td>`;
          }).join('')}
        </tr>`).join('')}</tbody>
      </table>
    </div>`;
}

function getPCSummaryPageItems(currentPage, totalPages) {
  if (totalPages <= 9) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const pages = new Set([1, totalPages, currentPage]);
  for (let page = currentPage - 1; page <= currentPage + 1; page += 1) {
    if (page > 1 && page < totalPages) pages.add(page);
  }
  if (currentPage <= 4) {
    [2, 3, 4, 5].forEach(page => pages.add(page));
  }
  if (currentPage >= totalPages - 3) {
    [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1]
      .forEach(page => pages.add(page));
  }

  const sortedPages = [...pages].filter(page => page >= 1 && page <= totalPages).sort((a, b) => a - b);
  return sortedPages.reduce((items, page, index) => {
    const previousPage = sortedPages[index - 1];
    if (previousPage && page - previousPage > 1) {
      items.push('ellipsis');
    }
    items.push(page);
    return items;
  }, []);
}

function renderPCSummaryPageButtons(currentPage, totalPages) {
  return getPCSummaryPageItems(currentPage, totalPages).map(item => {
    if (item === 'ellipsis') {
      return '<span class="pc-summary-page-ellipsis">...</span>';
    }

    return `
      <button
        type="button"
        class="btn btn-sm btn-outline-secondary pc-summary-page-btn ${item === currentPage ? 'is-active' : ''}"
        data-page="${item}"
        aria-label="Page ${item}"
        ${item === currentPage ? 'aria-current="page"' : ''}
      >${item}</button>
    `;
  }).join('');
}

function renderPCSummaryPanel(focusFilterDialog = false) {
  const rows = getPCSummaryRows();
  const createdPanel = !pcSummaryPanel;
  const filteredRows = sortPCSummaryRows(filterPCSummaryRows(rows));
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PC_SUMMARY_PAGE_SIZE));
  pcSummaryPage = Math.min(Math.max(Number(pcSummaryPage) || 1, 1), totalPages);
  const pageStart = (pcSummaryPage - 1) * PC_SUMMARY_PAGE_SIZE;
  const visibleRows = filteredRows.slice(pageStart, pageStart + PC_SUMMARY_PAGE_SIZE);

  if (!pcSummaryPanel) {
    pcSummaryPanel = document.createElement('div');
    pcSummaryPanel.id = 'pc-summary-panel';
    pcSummaryPanel.className = 'tool-panel';
    document.body.appendChild(pcSummaryPanel);
  }
  pcSummaryPanel.classList.toggle('pc-summary-settings-open', pcSummaryColumnSettingsOpen);

  const summaryColumns = getPCSummaryColumns();
  const visibleSummaryColumns = summaryColumns.filter(column => isPCSummaryColumnVisible(column.key));
  const summaryGroups = [...new Set(visibleSummaryColumns.map(column => column.group))];
  const visibleDistributionColumnCount = visibleSummaryColumns.filter(column => column.key !== 'addressCount').length;
  const preferredPanelWidth = 430
    + (isPCSummaryColumnVisible('addressCount') ? 84 : 0)
    + (visibleDistributionColumnCount * 68);
  pcSummaryPanel.style.setProperty('--pc-summary-preferred-width', `${preferredPanelWidth}px`);

  if (!pcSummaryDockButton) {
    pcSummaryDockButton = document.createElement('button');
    pcSummaryDockButton.type = 'button';
    pcSummaryDockButton.id = 'pc-summary-panel-dock';
    pcSummaryDockButton.className = 'pc-summary-panel-dock btn btn-light border border-start-0 shadow-sm';
    pcSummaryDockButton.innerHTML = '<i class="bi bi-chevron-left"></i>';
    pcSummaryDockButton.title = 'Restore Area Selection Summary';
    pcSummaryDockButton.setAttribute('aria-label', 'Restore Area Selection Summary');
    pcSummaryDockButton.style.display = 'none';
    document.body.appendChild(pcSummaryDockButton);
    registerDock('pc-summary', pcSummaryDockButton, 324, '.pc-panel-toggle');

    pcSummaryDockButton.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      showPCSummaryPanel();
    });
  }

  const showDownload = hasRight('data.download');
  pcSummaryPanel.innerHTML = `
    <div id="pc-summary-header">
      <b data-translate="pc_selection.summary_header">Area Selection Summary</b>
      <label class="summary-opacity-control" title="Transparency">
        <i class="bi bi-circle-half" aria-hidden="true"></i>
        <span class="summary-opacity-slider">
          <input id="pc-summary-opacity" type="range" min="30" max="100" step="5" value="${Math.round(pcSummaryOpacity * 100)}" aria-label="Area Selection Summary opacity">
          <output for="pc-summary-opacity" style="left:${7 + ((Math.round(pcSummaryOpacity * 100) - 30) / 70) * 40}px">${Math.round(pcSummaryOpacity * 100)}%</output>
        </span>
      </label>
      <button id="pc-summary-column-settings-btn" class="btn btn-sm btn-outline-secondary sevenn-panel-toggle" aria-label="Column settings" title="Column settings" aria-expanded="${pcSummaryColumnSettingsOpen}">
        <i class="bi bi-gear"></i>
      </button>
      <button id="pc-summary-minimize-btn" class="btn btn-sm btn-outline-secondary sevenn-panel-toggle" aria-label="Dock to left" title="Dock to left" data-translate-title="selection_aids.dock" data-translate-aria-label="selection_aids.dock">
        <i class="bi bi-dash-lg"></i>
      </button>
      <button id="pc-summary-close" class="btn-close btn-sm" aria-label="Close" title="Close"></button>
    </div>

    ${renderPCSummaryColumnSettings()}

    <div class="pc-summary-toolbar">
      <div class="pc-summary-tabs" role="tablist" aria-label="Area Selection Summary views">
        <button type="button" class="pc-summary-tab ${pcSummaryActiveTab === 'details' ? 'is-active' : ''}" data-summary-tab="details" role="tab" aria-selected="${pcSummaryActiveTab === 'details'}">Details</button>
        <button type="button" class="pc-summary-tab ${pcSummaryActiveTab === 'totals' ? 'is-active' : ''}" data-summary-tab="totals" role="tab" aria-selected="${pcSummaryActiveTab === 'totals'}">Totals</button>
      </div>
      <div class="pc-summary-toolbar-actions">
        <button type="button" class="btn btn-outline-secondary btn-sm pc-remove-selection ${pcRemoveSelectionActive ? 'active' : ''}" title="Remove Selection" aria-label="Remove Selection" aria-pressed="${pcRemoveSelectionActive}">
          <i class="bi bi-dash-circle fs-6" aria-hidden="true"></i>
        </button>
        <button type="button" class="btn btn-outline-danger btn-sm pc-clear-selection" ${pcSelectionDeleteInFlight ? 'disabled' : ''} title="Remove/Delete All" aria-label="Remove/Delete All">
          <i class="bi bi-trash fs-6" aria-hidden="true"></i>
        </button>
        ${showDownload ? `
          <button type="button" id="pc-summary-download-excel-btn" class="btn btn-sm btn-outline-secondary" title="Download Excel" aria-label="Download Excel" data-translate-title="draw_summary.download_excel" data-translate-aria-label="draw_summary.download_excel">
            <i class="bi bi-download fs-6" aria-hidden="true"></i>
          </button>
        ` : ``}
      </div>
    </div>

    <div id="pc-summary-table-container" ${pcSummaryActiveTab === 'details' ? '' : 'hidden'}>
      <table class="table table-sm table-bordered">
        <thead>
          <tr class="pc-summary-group-header">
            <th class="pc-summary-col-place" rowspan="2">
              ${renderPCSummaryHeaderCell('place', 'Place')}
            </th>
            <th class="pc-summary-col-postcode" rowspan="2">
              ${renderPCSummaryHeaderCell('postcode', 'PostCode')}
            </th>
            ${summaryGroups.map(group => `<th class="pc-summary-distributor-group distributor-${group.toLowerCase()}" colspan="${visibleSummaryColumns.filter(column => column.group === group).length}">${group}</th>`).join('')}
            <th class="pc-summary-col-options" rowspan="2" data-translate="draw_summary.options">Options</th>
          </tr>
          <tr>
            ${visibleSummaryColumns.map(column => `<th class="${column.key === 'addressCount' ? 'pc-summary-col-addresses' : 'pc-summary-col-distribution'} distributor-${column.group.toLowerCase()}">
              ${renderPCSummaryHeaderCell(column.key, column.label)}
            </th>`).join('')}
          </tr>
        </thead>
        <tbody>
          ${visibleRows.map(item => {
            const details = getPCSummaryDetails(item);
            return `
            <tr class="pc-summary-row" data-type="${item.type}" data-seq="${item.seq}" data-feature-id="${escapeHtml(details.id)}" data-external-area-search="${item.externalAreaSearch === true}">
              <td class="pc-summary-col-place" ${isPCSummaryColumnVisible('place') ? '' : 'hidden'}>${escapeHtml(details.place)}</td>
              <td class="pc-summary-col-postcode" ${isPCSummaryColumnVisible('postcode') ? '' : 'hidden'}>${escapeHtml(details.postcode)}</td>
              ${visibleSummaryColumns.map(column => `<td class="${column.key === 'addressCount' ? 'pc-summary-col-addresses' : 'pc-summary-col-distribution'}">${escapeHtml(getPCSummaryValue(item, column.key))}</td>`).join('')}
              <td class="pc-summary-col-options">
                <div class="draw-summary-actions">
                  <button class="pc-summary-delete-row-btn delete-row-btn" data-type="${item.type}" data-seq="${item.seq}" data-feature-id="${escapeHtml(details.id)}" data-row-index="${item.summaryRowIndex}" title="Delete" data-translate-title="draw_summary.delete_button">
                    <i class="bi bi-trash"></i>
                  </button>
                </div>
              </td>
            </tr>
          `}).join('')}
        </tbody>
      </table>
    </div>

    ${renderPCSummaryTotals(rows)}

    ${pcSummaryActiveTab === 'details' ? renderPCSummaryFilterDialog() : ''}

    <div id="pc-summary-pagination" class="pc-summary-pagination" ${pcSummaryActiveTab === 'details' ? '' : 'hidden'}>
      <button id="pc-summary-prev-page" class="btn btn-sm btn-outline-secondary" ${pcSummaryPage <= 1 ? 'disabled' : ''} aria-label="Previous page" title="Previous page">
        <i class="bi bi-chevron-left"></i>
      </button>
      <div class="pc-summary-page-list" aria-label="Area Selection Summary pages">
        ${renderPCSummaryPageButtons(pcSummaryPage, totalPages)}
      </div>
      <button id="pc-summary-next-page" class="btn btn-sm btn-outline-secondary" ${pcSummaryPage >= totalPages ? 'disabled' : ''} aria-label="Next page" title="Next page">
        <i class="bi bi-chevron-right"></i>
      </button>
      <span class="pc-summary-row-count">${filteredRows.length} rows</span>
    </div>

    <!-- Totals footer hidden; keep the dialog sized to its remaining content.
    <div class="draw-summary-footer pc-summary-footer" ${pcSummaryActiveTab === 'details' ? '' : 'hidden'}>
      <div id="pc-summary-total">
        <span class="pc-summary-total-item">Totals:</span>
      </div>
    </div>
    -->
  `;

  applyLanguageTranslations(pcSummaryPanel);
  applyLanguageTranslations(pcSummaryDockButton);

  if (pcSummaryMinimized) {
    pcSummaryPanel.classList.add('is-minimized');
    pcSummaryPanel.style.display = 'none';
    setDockVisible('pc-summary', pcSummaryMinimized);
  } else {
    pcSummaryPanel.classList.remove('is-minimized');
    pcSummaryPanel.style.display = 'block';
    setDockVisible('pc-summary', false);
    if (createdPanel) {
      requestAnimationFrame(() => placeFloatingDialog(pcSummaryPanel));
    }
  }

  pcSummaryPanel.querySelector('.pc-remove-selection')?.addEventListener('click', activatePCRemoveSelection);
  pcSummaryPanel.querySelector('.pc-clear-selection')?.addEventListener('click', clearPCSelection);
  pcSummaryPanel.querySelector('#pc-summary-minimize-btn')?.addEventListener('click', minimizePCSummaryPanel);
  pcSummaryPanel.querySelectorAll('.pc-summary-tab').forEach(button => button.addEventListener('click', event => {
    pcSummaryActiveTab = event.currentTarget.dataset.summaryTab === 'totals' ? 'totals' : 'details';
    pcSummaryColumnSettingsOpen = false;
    pcSummaryFilterDialogKey = null;
    renderPCSummaryPanel();
  }));
  pcSummaryPanel.querySelector('#pc-summary-column-settings-btn')?.addEventListener('click', event => {
    event.preventDefault();
    event.stopPropagation();
    pcSummaryColumnSettingsOpen = !pcSummaryColumnSettingsOpen;
    renderPCSummaryPanel();
  });
  pcSummaryPanel.querySelector('.pc-summary-column-settings-close')?.addEventListener('click', event => {
    event.preventDefault();
    pcSummaryColumnSettingsOpen = false;
    renderPCSummaryPanel();
  });
  pcSummaryPanel.querySelectorAll('.pc-summary-column-option input').forEach(input => {
    input.addEventListener('change', event => {
      event.stopPropagation();
      const key = input.dataset.columnKey;
      if (!key || !(key in pcSummaryColumnVisibility)) return;
      pcSummaryColumnVisibility[key] = input.checked;
      if (!input.checked) {
        pcSummaryFilters[key] = '';
        if (pcSummarySort.key === key) {
          const fallbackKey = getPCSummaryColumns().find(column => isPCSummaryColumnVisible(column.key))?.key || 'postcode';
          pcSummarySort = { key: fallbackKey, direction: 'asc' };
        }
      }
      renderPCSummaryPanel();
    });
  });
  pcSummaryPanel.querySelector('#pc-summary-close')?.addEventListener('click', closePCSummaryPanel);
  pcSummaryPanel.style.opacity = String(pcSummaryOpacity);
  let pcSummaryOpacityLabelTimer = null;
  pcSummaryPanel.querySelector('#pc-summary-opacity')?.addEventListener('input', event => {
    pcSummaryOpacity = Number(event.currentTarget.value) / 100;
    pcSummaryPanel.style.opacity = String(pcSummaryOpacity);
    const output = pcSummaryPanel.querySelector('output[for="pc-summary-opacity"]');
    if (output) {
      output.textContent = `${event.currentTarget.value}%`;
      output.style.left = `${7 + ((Number(event.currentTarget.value) - 30) / 70) * 40}px`;
      output.classList.add('is-visible');
      window.clearTimeout(pcSummaryOpacityLabelTimer);
      pcSummaryOpacityLabelTimer = window.setTimeout(() => output.classList.remove('is-visible'), 2000);
    }
  });
  pcSummaryPanel.querySelector('#pc-summary-download-excel-btn')?.addEventListener('click', event => {
    event.preventDefault();
    exportPCDrawSelections(event.currentTarget);
  });
  pcSummaryPanel.querySelector('#pc-summary-prev-page')?.addEventListener('click', event => {
    event.preventDefault();
    pcSummaryPage = Math.max(1, pcSummaryPage - 1);
    renderPCSummaryPanel();
  });
  pcSummaryPanel.querySelector('#pc-summary-next-page')?.addEventListener('click', event => {
    event.preventDefault();
    pcSummaryPage = Math.min(totalPages, pcSummaryPage + 1);
    renderPCSummaryPanel();
  });
  pcSummaryPanel.querySelectorAll('.pc-summary-page-btn').forEach(button => {
    button.addEventListener('click', event => {
      event.preventDefault();
      const page = Number(button.dataset.page) || 1;
      pcSummaryPage = Math.min(Math.max(page, 1), totalPages);
      renderPCSummaryPanel();
    });
  });
  pcSummaryPanel.querySelectorAll('.pc-summary-sort-btn').forEach(button => {
    button.addEventListener('click', event => {
      event.preventDefault();
      const key = button.dataset.sortKey;
      if (!key) return;
      pcSummarySort = {
        key,
        direction: pcSummarySort.key === key && pcSummarySort.direction === 'asc' ? 'desc' : 'asc'
      };
      pcSummaryPage = 1;
      renderPCSummaryPanel();
    });
  });
  pcSummaryPanel.querySelectorAll('.pc-summary-filter-btn').forEach(button => {
    button.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      const key = button.dataset.filterKey;
      if (!key || !(key in pcSummaryFilters)) return;
      pcSummaryFilterDialogKey = key;
      renderPCSummaryPanel(true);
    });
  });
  const filterInput = pcSummaryPanel.querySelector('.pc-summary-filter-dialog-input');
  const applyFilterDialog = () => {
    if (!pcSummaryFilterDialogKey || !(pcSummaryFilterDialogKey in pcSummaryFilters)) return;
    pcSummaryFilters[pcSummaryFilterDialogKey] = filterInput?.value || '';
    pcSummaryFilterDialogKey = null;
    pcSummaryPage = 1;
    renderPCSummaryPanel();
  };
  pcSummaryPanel.querySelector('.pc-summary-filter-apply')?.addEventListener('click', event => {
    event.preventDefault();
    applyFilterDialog();
  });
  pcSummaryPanel.querySelector('.pc-summary-filter-clear')?.addEventListener('click', event => {
    event.preventDefault();
    if (pcSummaryFilterDialogKey && pcSummaryFilterDialogKey in pcSummaryFilters) {
      pcSummaryFilters[pcSummaryFilterDialogKey] = '';
    }
    pcSummaryFilterDialogKey = null;
    pcSummaryPage = 1;
    renderPCSummaryPanel();
  });
  pcSummaryPanel.querySelector('.pc-summary-filter-cancel')?.addEventListener('click', event => {
    event.preventDefault();
    pcSummaryFilterDialogKey = null;
    renderPCSummaryPanel();
  });
  pcSummaryPanel.querySelector('.pc-summary-filter-close')?.addEventListener('click', event => {
    event.preventDefault();
    pcSummaryFilterDialogKey = null;
    renderPCSummaryPanel();
  });
  filterInput?.addEventListener('keydown', event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      applyFilterDialog();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      pcSummaryFilterDialogKey = null;
      renderPCSummaryPanel();
    }
  });
  filterInput?.addEventListener('click', event => event.stopPropagation());
  pcSummaryPanel.querySelectorAll('.pc-summary-delete-row-btn').forEach(button => {
    button.addEventListener('click', event => {
      event.preventDefault();
      event.stopPropagation();
      const row = button.closest('.pc-summary-row');
      if (row?.dataset.externalAreaSearch === 'true') {
        areaSearchSummaryRows = areaSearchSummaryRows.filter(item => item.summaryFeatureId !== button.dataset.featureId);
        pcSummaryPage = 1;
        renderPCSummaryPanel();
        return;
      }
      deletePCDrawSelection(
        button.dataset.type,
        button.dataset.seq,
        button.dataset.featureId,
        button.dataset.rowIndex
      );
    });
  });
  pcSummaryPanel.querySelectorAll('.pc-summary-row').forEach(row => {
    row.addEventListener('click', () => {
      pcSummaryPanel.querySelectorAll('.pc-summary-row').forEach(candidate => candidate.classList.remove('is-selected'));
      row.classList.add('is-selected');
      blinkPCSummarySelection(row.dataset.type, row.dataset.seq, row.dataset.featureId);
    });
  });

  const header = pcSummaryPanel.querySelector('#pc-summary-header');
  if (header) {
    makeDraggable(pcSummaryPanel, header);
  }

  if (createdPanel && rows.length) {
    activatePCRemoveSelection();
  }

  if (focusFilterDialog && filterInput) {
    filterInput.focus();
    if (filterInput.type !== 'number') {
      const cursorPosition = filterInput.value.length;
      filterInput.setSelectionRange(cursorPosition, cursorPosition);
    }
  }
}

function fitPCSelectionLayerToFeatures(features) {
  const currentMap = map || getMap();
  if (!currentMap || !features.length) return;

  const extent = features.reduce((acc, feature) => {
    const geometry = feature.getGeometry?.();
    if (!geometry) return acc;
    const featureExtent = geometry.getExtent();
    if (!acc) return featureExtent.slice();
    acc[0] = Math.min(acc[0], featureExtent[0]);
    acc[1] = Math.min(acc[1], featureExtent[1]);
    acc[2] = Math.max(acc[2], featureExtent[2]);
    acc[3] = Math.max(acc[3], featureExtent[3]);
    return acc;
  }, null);

  if (!extent) return;

  currentMap.getView().fit(extent, {
    padding: [70, 70, 70, 70],
    duration: 700,
    maxZoom: 18
  });
}

function fitPCSelectionResultsFromLayer(options = {}) {
  const currentMap = map || getMap();
  const source = getVecSelectionLayer()?.getSource?.();
  const features = source?.getFeatures?.() || [];
  if (!currentMap || !features.length) return false;

  fitPCSelectionLayerToFeatures(features);
  return true;
}

function scheduleFinalPCSelectionFit() {
  requestAnimationFrame(() => {
    fitPCSelectionResultsFromLayer();
  });
}

async function blinkPCSummarySelection(type, seq, featureId = '') {
  const selectionLayer = getVecSelectionLayer();
  const source = selectionLayer?.getSource?.();
  const key = normalizePCType(type);
  const sequence = Number(seq) || 0;
  const prefix = `${key}:${sequence}:`;
  const targetFeatureId = String(featureId || '').trim();
  if (!source || !key || !sequence) return;

  let features = source.getFeatures()
    .filter(feature => {
      const id = String(feature?.getId?.() || '');
      if (!id.startsWith(prefix)) return false;
      if (!targetFeatureId) return true;

      // Restored selections can retain the original source id while the
      // enriched PC4 feature now resolves its public identifier to postcode.
      // The composite selection-layer id remains tied to the stored row id,
      // so accept either representation when locating the feature to blink.
      return id === `${prefix}${targetFeatureId}`
        || getPCFeatureIdentifier(feature) === targetFeatureId;
    });
  const temporaryFeatures = [];

  const addTemporaryFeatures = () => {
    const selection = getSelectionByTypeAndSeq(key, sequence);
    const properties = getPCSelectionPropertiesPayload(selection)
      .filter(property => {
        const id = getPCSelectionPropertyId(property);
        return id && (!targetFeatureId || id === targetFeatureId);
      });

    properties.forEach((property, index) => {
      const propertyFeatureId = getPCSelectionPropertyId(property);
      const feature = findPCFeatureById(key, propertyFeatureId);
      if (!feature?.clone) return;

      const clone = feature.clone();
      clone.setId(`blink:${prefix}${propertyFeatureId || index}`);
      clone.set('_pcAreaType', key.toUpperCase());
      clone.setStyle(createStyleFromColor(getPCSelectionColor(key)));
      temporaryFeatures.push(clone);
    });

    if (temporaryFeatures.length) {
      source.addFeatures(temporaryFeatures);
      features = temporaryFeatures;
    }
  };

  if (!features.length) {
    addTemporaryFeatures();
  }

  if (!features.length && targetFeatureId) {
    triggerPCLayerLoadForResults([{
      type: key,
      seq: sequence,
      properties: JSON.stringify([{ id: targetFeatureId }])
    }], { fullExtent: true });

    for (let attempt = 0; attempt < 4 && !features.length; attempt += 1) {
      await waitForPCFeatureLoadAttempt(attempt < 2 ? 250 : 500);
      addTemporaryFeatures();
    }
  }

  if (!features.length) {
    setStatus('Area Selection feature is not available on the map yet');
    return;
  }

  const originalStyles = new Map(features.map(feature => [feature, feature.getStyle?.()]));
  let ticks = 0;
  let timer = null;
  const maxTicks = PC_SUMMARY_BLINK_COUNT * 2;

  const updateBlink = () => {
    const showBlink = ticks % 2 === 0;
    features.forEach(feature => {
      feature.setStyle(showBlink ? pcSummaryBlinkStyle : originalStyles.get(feature));
    });
    source.changed?.();
    ticks += 1;

    if (ticks >= maxTicks) {
      window.clearInterval(timer);
      features.forEach(feature => {
        feature.setStyle(originalStyles.get(feature));
      });
      temporaryFeatures.forEach(feature => source.removeFeature(feature));
      source.changed?.();
    }
  };

  // Show the first blink frame immediately so every grid-cell click has
  // instant visual feedback on the corresponding map feature.
  updateBlink();
  if (ticks < maxTicks) {
    timer = window.setInterval(updateBlink, PC_SUMMARY_BLINK_INTERVAL_MS);
  }
}

function highlightLoadedPCSelections(selections, options = {}) {
  const selectionLayer = getVecSelectionLayer();
  const source = selectionLayer?.getSource?.();
  if (!source) return [];

  source.clear();
  pcSelectedAreaFeatureCache.forEach(cache => cache.clear());
  if (options.emitMessages !== false) {
    sendPCAreaRemovalMessages();
  }
  selectedFeaturesMap.clear();

  const highlightedFeatures = [];

  selections.forEach(selection => {
    const type = normalizePCType(getPCSelectionType(selection));
    const color = getPCSelectionColor(type);
    const properties = getPCSelectionPropertiesPayload(selection);
    selection.addressCount = 0;

    properties.forEach(property => {
      const feature = findPCFeatureById(type, getPCSelectionLookupId(property, type));
      if (!feature) return;

      const clone = feature.clone();
      const featureId = getPCFeatureIdentifier(feature);
      clone.setId(`${type}:${selection.seq}:${featureId || highlightedFeatures.length}`);
      clone.set('_pcAreaType', type.toUpperCase());
      clone.setStyle(createStyleFromColor(color));
      if (pcSelectedAreaVisibility.get(type.toUpperCase()) === false) pcSelectedAreaFeatureCache.get(type.toUpperCase())?.set(clone.getId(), clone);
      else { selectedFeaturesMap.set(clone.getId(), clone); highlightedFeatures.push(clone); }
      selection.addressCount = (Number(selection.addressCount) || 0) + getPCFeatureAddressCount(feature);
      if (options.emitMessages !== false && pcSelectedAreaVisibility.get(type.toUpperCase()) !== false) {
        sendPCAreaMessage('add', clone);
      }
    });

    setPCDrawSelectionAddressCount(type, selection.seq, selection.addressCount);
  });

  source.addFeatures(highlightedFeatures);
  if (!options.silent) updateSelectionStatus();
  if (options.fitToResults !== false) {
    fitPCSelectionLayerToFeatures(highlightedFeatures);
  }
  return highlightedFeatures;
}

function getPCSelectionExpectedFeatureCount(selections) {
  return selections.reduce((total, selection) => {
    return total + getPCSelectionPropertiesPayload(selection).length;
  }, 0);
}

function triggerPCLayerLoadForResults(selections, options = {}) {
  const currentMap = map || getMap();
  if (!currentMap?.getView) return;

  const view = currentMap.getView();
  const extent = options.fullExtent
    ? WEB_MERCATOR_WORLD_EXTENT
    : view.calculateExtent(currentMap.getSize());
  const resolution = view.getResolution();
  const projection = view.getProjection();
  const types = new Set(selections.map(selection => getPCSelectionType(selection).toUpperCase()).filter(Boolean));

  types.forEach(type => {
    getSelectableLayers(type).forEach(layer => {
      const source = layer.get?.('wfsSource') || layer.getSource?.();
      source?.loadFeatures?.(extent, resolution, projection);
    });
  });
}

async function loadPCSelectionFeaturesById(selections) {
  const idsByType = new Map();

  (selections || []).forEach(selection => {
    const type = getPCSelectionType(selection).toUpperCase();
    if (!type) return;
    const ids = idsByType.get(type) || new Set();
    getPCSelectionPropertiesPayload(selection)
      .map(property => getPCSelectionLookupId(property, type))
      .filter(Boolean)
      .forEach(id => ids.add(id));
    idsByType.set(type, ids);
  });

  await Promise.all([...idsByType.entries()].map(async ([type, ids]) => {
    for (const layer of getSelectableLayers(type)) {
      const source = layer.get?.('wfsSource') || layer.getSource?.();
      const typeName = layer.get?.('typeName');
      if (!source || !typeName || !ids.size) continue;

      const missingIds = [...ids].filter(id => !findPCFeatureById(type, id));
      if (!missingIds.length) continue;

      const cqlValues = missingIds.map(id => `'${String(id).replace(/'/g, "''")}'`);
      const normalizedType = normalizePCType(type);
      const cqlField = String(typeName).toLowerCase() === 'geolinq:be_4pp'
        ? (Object.hasOwn(source.getFeatures?.()[0]?.getProperties?.() || {}, '4pp') ? '4pp' : 'postcode')
        : normalizedType === 'nis9'
        ? 'niscode'
        : normalizedType === 'nis6'
        ? 'nis6'
        : normalizedType === 'pc6' && isBelgiumPCSummary()
          ? 'nouveau_po'
          : normalizedType === 'pc6'
            ? 'u104986pc6_cbs'
            : normalizedType === 'pc4'
              ? 'u104987pc4_cbs'
              : 's_source_id';
      const params = new URLSearchParams({
        service: 'WFS',
        version: '1.0.0',
        request: 'GetFeature',
        typename: typeName,
        outputFormat: 'application/json',
        srsname: 'EPSG:3857',
        CQL_FILTER: `${cqlField} IN (${cqlValues.join(',')})`
      });

      try {
        let requestUrl = `${config.GEOSERVER_URL}?${params.toString()}`;
        const useGeometryApi = !isBelgiumPCSummary() && (normalizedType === 'pc4' || normalizedType === 'pc6');
        if (useGeometryApi) {
          const restorationParams = new URLSearchParams({ type: normalizedType });
          missingIds.forEach(id => restorationParams.append('ids[]', String(id)));
          requestUrl = `${config.API_URL}spatial-search/pc-features?${restorationParams.toString()}`;
        }
        // Public WFS uses the same anonymous GET as the map's VectorSource.
        // JWT/JSON headers would trigger a cross-origin preflight that the
        // GeoServer endpoint does not allow.
        const response = useGeometryApi
          ? await secureFetch(requestUrl)
          : await fetch(requestUrl, { headers: { Accept: 'application/json' } });
        if (!response.ok) continue;
        const geojson = await response.json();
        const features = new GeoJSON({ geometryName: '__mapGeometry' }).readFeatures(geojson, {
          dataProjection: 'EPSG:3857',
          featureProjection: 'EPSG:3857'
        });
        features.forEach(feature => {
          const id = getPCFeatureIdentifier(feature) || String(feature.getId?.() ?? '');
          if (id) feature.setId(id);
          if (!id || !source.getFeatureById?.(id)) source.addFeature(feature);
        });
      } catch (error) {
        console.warn(`[PCSelection] Direct ${type} feature restoration failed`, error);
      }
    }
  }));
}

function waitForPCFeatureLoadAttempt(delayMs = 300) {
  return new Promise(resolve => setTimeout(resolve, delayMs));
}

async function waitForPCSelectableLayers(selections, options = {}) {
  const types = [...new Set(
    (selections || [])
      .map(selection => getPCSelectionType(selection).toUpperCase())
      .filter(Boolean)
  )];
  if (!types.length) return;

  const maxAttempts = Number.isFinite(options.maxAttempts) ? options.maxAttempts : 10;
  const delayMs = Number.isFinite(options.delayMs) ? options.delayMs : 250;

  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    const allTypesReady = types.every(type => getSelectableLayers(type).length > 0);
    if (allTypesReady) return;
    await waitForPCFeatureLoadAttempt(delayMs);
  }
}

async function highlightLoadedPCSelectionsWhenReady(selections, options = {}) {
  const expectedCount = getPCSelectionExpectedFeatureCount(selections);
  let highlighted = [];
  const maxAttempts = Number.isFinite(options.maxAttempts) ? options.maxAttempts : 16;

  let fittedCount = 0;
  for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
    highlighted = highlightLoadedPCSelections(selections, {
      fitToResults: false,
      emitMessages: options.emitMessages
    });

    if (options.progressiveFit && highlighted.length > fittedCount) {
      fitPCSelectionLayerToFeatures(highlighted);
      fittedCount = highlighted.length;
    }
    if (!expectedCount || highlighted.length >= expectedCount) break;

    // Direct ID restoration normally supplies every geometry. Only request
    // a broader extent when geometry is genuinely missing.
    if (attempt === 0) triggerPCLayerLoadForResults(selections, {
      fullExtent: options.loadFullExtent === true
    });
    await waitForPCFeatureLoadAttempt(attempt < 2 ? 250 : 450);
  }

  if (expectedCount && highlighted.length < expectedCount) {
    triggerPCLayerLoadForResults(selections, { fullExtent: true });
    await waitForPCFeatureLoadAttempt(700);
    highlighted = highlightLoadedPCSelections(selections, {
      fitToResults: false,
      emitMessages: options.emitMessages
    });
  }

  if (options.fitToResults !== false && highlighted.length) {
    fitPCSelectionLayerToFeatures(highlighted);
  }

  return highlighted;
}

function highlightPCDrawSelectionFeatures(selection, features) {
  const selectionLayer = getVecSelectionLayer();
  const source = selectionLayer?.getSource?.();
  if (!source || !selection || !Array.isArray(features)) return;

  const type = normalizePCType(selection.type);
  const seq = Number(selection.seq) || 0;
  const prefix = `${type}:${seq}:`;

  [...selectedFeaturesMap.keys()]
    .filter(key => String(key).startsWith(prefix))
    .forEach(key => selectedFeaturesMap.delete(key));

  source.clear();

  features.forEach((feature, index) => {
    if (!feature?.clone) return;
    const featureId = getPCFeatureIdentifier(feature) || index;
    const clone = feature.clone();
    clone.setId(`${prefix}${featureId}`);
    clone.set('_pcAreaType', type.toUpperCase());
    clone.setStyle(createStyleFromColor(getPCSelectionColor(type)));
    selectedFeaturesMap.set(clone.getId(), clone);
    sendPCAreaMessage('add', clone);
  });

  setPCDrawSelectionAddressCount(type, seq, Number(selection.addressCount) || 0);
  source.addFeatures(Array.from(selectedFeaturesMap.values()));
  updateSelectionStatus();
}

/** ================================
 * Click-based PC Selection
 * ================================ */
function setPCRemoveSelectionActive(active) {
  pcRemoveSelectionActive = active;
  const button = pcSummaryPanel?.querySelector('.pc-remove-selection');
  button?.classList.toggle('active', active);
  button?.setAttribute('aria-pressed', String(active));
}

function activatePCRemoveSelection() {
  map = getMap();
  if (!map) return;
  preserveLegendDuringAreaSelectionActivation();
  notifyPCSelectionActivating();
  bindExternalCancelOnce();
  stopPCSelectionInteraction();

  const selectionLayer = getVecSelectionLayer();
  const interaction = new Select({
    condition: click,
    layers: layer => layer === selectionLayer,
    style: null,
    hitTolerance: 3
  });
  pcSelectInteraction = interaction;
  map.addInteraction(interaction);
  isActive = true;
  setPCRemoveSelectionActive(true);
  setCursor('pointer');
  bindPcEscapeHandlerOnce();

  interaction.on('select', async event => {
    const features = [...event.selected];
    interaction.getFeatures().clear();
    if (pcRowDeleteInFlight || pcSelectionDeleteInFlight || pcDrawLoadInFlight) return;
    pcRowDeleteInFlight = true;
    try {
      for (const feature of features) {
        const id = String(feature.getId?.() || '');
        const type = normalizePCType(feature.get('_pcAreaType'));
        const selection = (pcDrawSelectionsByType.get(type) || []).find(item => id.startsWith(`${type}:${item.seq}:`));
        if (!selection) continue;
        const lookupId = id.slice(`${type}:${selection.seq}:`.length);
        const properties = getPCSelectionPropertiesPayload(selection);
        const rowIndex = properties.findIndex(property => getPCSelectionLookupId(property, type) === lookupId || getPCSelectionPropertyId(property) === lookupId);
        if (rowIndex < 0) continue;
        await deletePCDrawSelection(type, selection.seq, getPCSelectionPropertyId(properties[rowIndex]), rowIndex, { silent: true });
      }
    } finally {
      pcRowDeleteInFlight = false;
    }
  });
}

// Click selection toggles persisted rows, including selections restored at startup.
async function togglePCFeatureSelections(level, features) {
  if (pcRowDeleteInFlight || pcSelectionDeleteInFlight || pcDrawLoadInFlight) return;
  pcRowDeleteInFlight = true;
  try {
    const type = normalizePCType(level);
    const additions = getUnselectedPCFeatures(level, features);
    for (const feature of features.filter(item => !additions.includes(item))) {
      const id = getPCFeatureIdentifier(feature);
      const lookupId = getPCSelectionLookupId({ ...feature.getProperties?.(), id }, type);
      // Resolve each row again after deletion, since its index may have changed.
      for (const selection of [...(pcDrawSelectionsByType.get(type) || [])]) {
        const properties = getPCSelectionPropertiesPayload(selection);
        const rowIndex = properties.findIndex(property =>
          getPCSelectionPropertyId(property) === id
          || (lookupId && getPCSelectionLookupId(property, type) === lookupId));
        if (rowIndex < 0) continue;
        await deletePCDrawSelection(type, selection.seq, getPCSelectionPropertyId(properties[rowIndex]), rowIndex);
      }
    }
    if (additions.length) {
      const savedCount = await trySavePCDrawSelection(level, additions);
      if (savedCount) setStatus(`${savedCount} ${level} feature(s) saved`);
    }
  } finally {
    pcRowDeleteInFlight = false;
  }
}

export function activatePCSelection(pcType = 'PC4') {
  setPCRemoveSelectionActive(false);

  map = getMap();
  if (!map) {
    //console.warn("PC selection called before map ready");
    return;
  }

  const availability = updatePCSelectionButtons(map);
  const level = String(pcType || '').trim().toUpperCase();
  if (!availability.visibleLevels.has(level)) {
    setStatus(availability.reason);
    setCommand("Area Selections unavailable");
    return;
  }

  preserveLegendDuringAreaSelectionActivation();
  notifyPCSelectionActivating();
  bindExternalCancelOnce();


  // Remove existing interaction if any
  if (pcSelectInteraction) map.removeInteraction(pcSelectInteraction);


  pcSelectInteraction = new Select({
    condition: click,
    multi: true,
    layers: l => getSelectableLayers(level).includes(l), // follow layers refreshed after zoom
    style: null // use custom highlight layer
  });

  map.addInteraction(pcSelectInteraction);
  isActive = true;
  setPCDialogActiveTool(level === 'NIS6' ? '.nis6-select' : `.pc${level.slice(2)}-select`);
  activePCSelectionLevel = level;
  setCommand(`Select ${getPCSelectionLayerTitle(level)}`);
  setStatus(`Please select ${pcType}`);
  setCursor('pointer');
  bindPcEscapeHandlerOnce();

  pcSelectInteraction.on('select', async e => {
    const selectedFeatures = e.selected.filter(feature => getPCFeatureIdentifier(feature));

    e.selected.forEach(feature => pcSelectInteraction.getFeatures().remove(feature));
    e.deselected.forEach(feature => pcSelectInteraction.getFeatures().remove(feature));

    if (!selectedFeatures.length) {
      setStatus(`No ${level} feature identifier found for the selected area`);
      return;
    }

    await withBusyCursor(() => togglePCFeatureSelections(level, selectedFeatures));
  });
}

// Navigation can restore the current area command after a temporary zoom tool.
export function getPCSelectionResumeAction() {
  const level = activePCSelectionLevel;
  return level ? () => {
    const currentMap = getMap();
    if (currentMap && getPCSelectionAvailability(currentMap).visibleLevels.has(level)) {
      activatePCSelection(level);
    }
  } : null;
}

export function deactivatePCSelection() {
  activePCSelectionLevel = null;
  setPCRemoveSelectionActive(false);
  if (pcSelectInteraction) map.removeInteraction(pcSelectInteraction);
  if (pcDrawInteraction) map.removeInteraction(pcDrawInteraction);
  pcSelectInteraction = null;
  pcDrawInteraction = null;
  pcDrawSessionActive = false;
  isActive = false;
  if (vecSource) vecSource.clear();
  setCursor('');
  setCommand('Ready');
  sendPCAreaRemovalMessages();
  selectedFeaturesMap.clear();
  getVecSelectionLayer().getSource().clear();
  updateSelectionStatus();
  setPCDialogActiveTool(null);
  stopDrawAutoPan();

}

function stopPCSelectionInteraction() {
  activePCSelectionLevel = null;
  setPCRemoveSelectionActive(false);

  if (!map) map = getMap();
  if (!map) return;

  if (pcSelectInteraction) {
    map.removeInteraction(pcSelectInteraction);
    pcSelectInteraction = null;
  }

  if (pcDrawInteraction) {
    map.removeInteraction(pcDrawInteraction);
    pcDrawInteraction = null;
  }

  pcDrawSessionActive = false;
  setCursor('');
  setPCDialogActiveTool(null);
  stopDrawAutoPan();
}

document.addEventListener('application-reset-active-tools', () => {
  stopPCSelectionInteraction();
  setPCDialogActiveTool(null);
});

/** ================================
 * Draw-based selection
 * ================================ */
function initVecLayer() {

  if (!map) map = getMap();
  if (!map) return;

  if (!vecSource) {
    vecSource = new VectorSource({ wrapX: false });
    const drawLayer = new VectorLayer({ source: vecSource, style: drawStyle });
    map.addLayer(drawLayer);
  }

}

function fetchWithTimeout(promise, timeoutMs = 8000) {
  let timeoutId;

  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => reject(new Error('Backend request timed out')), timeoutMs);
  });

  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
}

async function sendPCSelection(geometry, pcType, drawGeometry = null, options = {}) {

  const ctx = getSessionContext();

  if (!ctx?.countryId || !ctx?.languageId) {
    setStatus('Please select country and language first');
    return;
  }

  setStatus(`Loading ${pcType} selection...`);

  try {
    const res = await fetchWithTimeout(secureFetch(`${config.API_URL}spatial-search/pc-select?pcType=${pcType}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        geometry,
        ...ctx
      }),
    }));

    const text = await res.text();
    const data = text ? JSON.parse(text) : null;

    if (!res.ok) {
      throw new Error(data?.message || text || `Server returned ${res.status}`);
    }

    const features = data?.features || [];
    const selectedFeatures = features.map(f => {
      return new GeoJSON().readFeature(f, {
        dataProjection: 'EPSG:4326',
        featureProjection: map.getView().getProjection(),
      });
    });

    if (features.length) {
      if (await trySavePCDrawSelection(pcType, selectedFeatures) === null) return [];
      setStatus(`${features.length} ${pcType} feature(s) found`);
      return selectedFeatures;
    }

    const localFeatures = await selectLoadedPCFeaturesForDraw(drawGeometry, pcType, options);
    if (await trySavePCDrawSelection(pcType, localFeatures) === null) return [];
    setStatus(`${localFeatures.length} ${pcType} feature(s) found`);
    return localFeatures;
  } catch (err) {
    console.error('PC selection failed:', err);
    const localFeatures = await selectLoadedPCFeaturesForDraw(drawGeometry, pcType, options);
    if (await trySavePCDrawSelection(pcType, localFeatures) === null) return [];
    setStatus(`${localFeatures.length} ${pcType} feature(s) found`);
    return localFeatures;
  }
}

function getActivePCType() {
  const currentMap = map || getMap();
  const availability = getPCSelectionAvailability(currentMap);
  if (!availability.allowed) return null;
  // Use exactly the same effective visibility and permission checks as the UI.
  // At shared zoom boundaries, the more detailed layer takes priority.
  return ['NIS9', 'PC6', 'NIS6', 'PC4']
    .find(level => availability.visibleLevels.has(level)) || null;
}

// Radius around a company found through Search Location, independent of the
// currently visible administrative level. Layer Info POI analysis stays separate.
export async function selectSearchCompanyRadiusAreas({ feature, radiusKm }) {
  map = getMap();
  const radius = Number(radiusKm);
  if (!map) throw new Error('Map is not ready');
  if (!hasRight('data.pcSelection')) throw new Error('Area selection permission is required');
  if (!Number.isFinite(radius) || radius <= 0) throw new Error('Enter a radius greater than zero');
  const country = String(getSessionContext()?.countryId || '');
  if (!['1', '4'].includes(country)) throw new Error('Company radius selection is available for Netherlands and Belgium');
  const geometry = feature?.getGeometry?.();
  if (geometry?.getType?.() !== 'Point') throw new Error('The selected company has no valid point location');
  const projection = map.getView().getProjection();
  const center = toLonLat(geometry.getCoordinates(), projection);
  if (!center.every(Number.isFinite)) throw new Error('The selected company has no valid coordinates');
  const pcType = country === '1' ? 'PC4' : 'PC6';
  const level = country === '1' ? 'pc4' : 'postalcode';
  const polygon = circular(center, radius * 1000, 128);
  const mapPolygon = polygon.clone().transform('EPSG:4326', projection);
  preserveLegendDuringAreaSelectionActivation();
  notifyPCSelectionActivating();
  bindExternalCancelOnce();
  stopPCSelectionInteraction();
  pcSummaryExplicitlyOpened = true;
  pcSummaryMinimized = false;
  setPCDialogActiveTool('.pc-circle');
  try {
    return await withBusyCursor(async () => {
      const selectedFeatures = await sendPCSelection(
        new GeoJSON().writeGeometryObject(polygon), pcType, mapPolygon, { includeHidden: true }
      ) || [];
      renderPCSummaryPanel();
      showPCSummaryPanel();
      if (selectedFeatures.length) fitPCSelectionLayerToFeatures(selectedFeatures);
      return { level, selectedFeatures, radiusKm: radius };
    });
  } finally {
    setPCDialogActiveTool(null);
  }
}

export function drawPCPolygon() {

  map = getMap();
  if (!map) return;
  if (!canUsePCSelectionDraw(map)) return;
  let pcType = getActivePCType();
  if (!pcType) return;

  preserveLegendDuringAreaSelectionActivation();
  notifyPCSelectionActivating();
  bindExternalCancelOnce();
  stopPCSelectionInteraction();
  initVecLayer();
  pcDrawSessionActive = true;
  setPCDialogActiveTool('.pc-polygon');
  setCommand('PCs selection using irregular polygon');
  setStatus('Draw polygon');
  bindPcEscapeHandlerOnce();

  function startDraw() {
    pcType = getActivePCType();
    if (!pcType) return;
    const drawingPCType = pcType;

    pcDrawInteraction = new Draw({ source: vecSource, type: 'Polygon' });
    const activeDraw = pcDrawInteraction;
    pcDrawInteraction.on('drawstart', () => startDrawAutoPan(map, PC_DRAW_AUTOPAN));
    map.addInteraction(pcDrawInteraction);
    setCursor('crosshair');

    pcDrawInteraction.once('drawend', async evt => {
      // Stop consuming map events before waiting for the selection API.
      map.removeInteraction(activeDraw);
      if (pcDrawInteraction === activeDraw) pcDrawInteraction = null;
      stopDrawAutoPan();

      const drawGeometry = evt.feature.getGeometry().clone();
      const geom = drawGeometry.clone().transform('EPSG:3857', 'EPSG:4326');

      await sendPCSelection(
        new GeoJSON().writeGeometryObject(geom),
        drawingPCType,
        drawGeometry
      );

      requestAnimationFrame(() => vecSource.clear());

      if (pcDrawSessionActive && !pcDrawInteraction) {
        startDraw(); // continue until ESC
      }
    });
  }

  startDraw();
}

export function drawPCCircle() {

  map = getMap();
  if (!map) return;
  if (!canUsePCSelectionDraw(map)) return;
  let pcType = getActivePCType();
  if (!pcType) return;

  preserveLegendDuringAreaSelectionActivation();
  notifyPCSelectionActivating();
  bindExternalCancelOnce();
  stopPCSelectionInteraction();
  initVecLayer();
  pcDrawSessionActive = true;
  setPCDialogActiveTool('.pc-circle');
  setCommand('PCs selection using irregular circle');
  setStatus('Draw circle');
  bindPcEscapeHandlerOnce();

  function startDraw() {
    pcType = getActivePCType();
    if (!pcType) return;
    const drawingPCType = pcType;

    pcDrawInteraction = new Draw({
      source: vecSource,
      type: 'Circle',
      style: createRadiusDrawStyle()
    });
    const activeDraw = pcDrawInteraction;
    attachRadiusDrawLabel(pcDrawInteraction, map);
    pcDrawInteraction.on('drawstart', () => startDrawAutoPan(map, PC_DRAW_AUTOPAN));
    map.addInteraction(pcDrawInteraction);
    setCursor('crosshair');

    pcDrawInteraction.once('drawend', async evt => {
      map.removeInteraction(activeDraw);
      if (pcDrawInteraction === activeDraw) pcDrawInteraction = null;
      stopDrawAutoPan();

      const poly = fromCircle(evt.feature.getGeometry(), 64);
      const drawGeometry = poly.clone();
      const geo = poly.clone().transform('EPSG:3857', 'EPSG:4326');

      await sendPCSelection(
        new GeoJSON().writeGeometryObject(geo),
        drawingPCType,
        drawGeometry
      );

      requestAnimationFrame(() => vecSource.clear());

      if (pcDrawSessionActive && !pcDrawInteraction) {
        startDraw(); // continue until ESC
      }
    });
  }

  startDraw();
}

function confirmDeleteAllPCSelections() {
  return new Promise(resolve => {
    const previousFocus = document.activeElement;
    const dialog = document.createElement('dialog');
    dialog.className = 'pc-delete-all-dialog';
    dialog.setAttribute('aria-labelledby', 'pc-delete-all-title');
    dialog.setAttribute('aria-describedby', 'pc-delete-all-message');
    dialog.innerHTML = `
      <div class="sevenn-dialog-header">
        <h2 id="pc-delete-all-title" class="sevenn-dialog-title">Remove/Delete All</h2>
      </div>
      <div class="sevenn-dialog-body">
        <p id="pc-delete-all-message">Do you want to permanently delete all saved area selections?</p>
      </div>
      <div class="sevenn-dialog-footer">
        <button type="button" class="btn btn-outline-danger" data-delete-all-yes>Yes</button>
        <button type="button" class="btn btn-outline-secondary" data-delete-all-no autofocus>No</button>
      </div>
    `;
    const finish = confirmed => {
      dialog.close();
      dialog.remove();
      if (previousFocus?.isConnected) previousFocus.focus();
      resolve(confirmed);
    };
    dialog.querySelector('[data-delete-all-yes]').addEventListener('click', () => finish(true));
    dialog.querySelector('[data-delete-all-no]').addEventListener('click', () => finish(false));
    dialog.addEventListener('cancel', event => {
      event.preventDefault();
      event.stopPropagation();
      finish(false);
    });
    // Keep Escape inside the modal so it does not cancel the map tool.
    dialog.addEventListener('keydown', event => event.stopPropagation());
    document.body.appendChild(dialog);
    dialog.showModal();
    dialog.querySelector('[data-delete-all-no]').focus();
  });
}

export async function clearPCSelection() {
  if (pcSelectionDeleteInFlight || pcRowDeleteInFlight) return;
  pcSelectionDeleteInFlight = true;
  const clearButton = pcSummaryPanel?.querySelector('.pc-clear-selection');
  if (clearButton) clearButton.disabled = true;
  let deletionConfirmed = false;

  try {
    if (!await confirmDeleteAllPCSelections()) return;
    deletionConfirmed = true;
    showLoadingBar();
    // A mixed-type restore can still be enriching rows when Delete All is
    // clicked. Wait for it rather than ignoring the action or deleting a
    // partial snapshot. New restores are blocked until deletion finishes.
    if (pcDrawLoadInFlight) {
      setStatus('Waiting for Area Selections to finish loading before deleting...');
      await new Promise(resolve => {
        document.addEventListener('pc-current-results-loaded', resolve, { once: true });
      });
    }
    const failedTypes = [];
    // Saving an empty list deletes all persisted selections of that type.
    // Keep failed types in memory so they remain visible and can be retried.
    for (const [type, selections] of [...pcDrawSelectionsByType.entries()]) {
      pcDrawSelectionsByType.delete(type);
      try {
        await persistPCDrawSelections(type);
      } catch (error) {
        pcDrawSelectionsByType.set(type, selections);
        failedTypes.push(type);
        console.error(`Area Selection delete failed for ${type}:`, error);
      }
    }

    areaSearchSummaryRows = [];
    if (failedTypes.length) {
      highlightLoadedPCSelections(getPCDrawSelections(), { fitToResults: false });
      pcSummaryPage = 1;
      renderPCSummaryPanel();
      setStatus(`Area Selection delete failed for ${failedTypes.join(', ')}. Remaining selections kept; please try again.`);
      return;
    }
    sendPCAreaRemovalMessages();
    selectedFeaturesMap.clear();
    pcSelectedAreaFeatureCache.forEach(cache => cache.clear());
    getVecSelectionLayer()?.getSource()?.clear();
    pcSummaryPage = 1;
    renderPCSummaryPanel();
    setStatus('All Area Selections deleted');
  } catch (error) {
    console.error('Area Selections delete failed:', error);
    highlightLoadedPCSelections(getPCDrawSelections(), { fitToResults: false });
    renderPCSummaryPanel();
    setStatus('Area Selections delete failed. Remaining selections kept; please try again.');
  } finally {
    pcSelectionDeleteInFlight = false;
    const currentClearButton = pcSummaryPanel?.querySelector('.pc-clear-selection');
    if (currentClearButton) currentClearButton.disabled = false;
    if (deletionConfirmed) hideLoadingBar();
  }
}

export async function loadPCDrawFromBackend(options = {}) {
  // A user click must reveal the summary even if another load is in flight,
  // no saved selections exist, or WFS enrichment is still pending.
  if (options.showImmediately === true) {
    pcSummaryExplicitlyOpened = true;
    pcSummaryMinimized = false;
    renderPCSummaryPanel();
    showPCSummaryPanel();
  }
  if (pcDrawLoadInFlight || pcSelectionDeleteInFlight) return [];
  pcDrawLoadInFlight = true;
  const summaryVisibilityToken = pcSummaryDeferredOpenToken;

  map = getMap();
  const ctx = getSessionContext();

  if (!map || !ctx?.countryId || !ctx?.languageId) {
    pcDrawLoadInFlight = false;
    if (!options.silent) {
      setStatus('Please select country and language first');
    }
    notifyPCCurrentResultsLoaded({ success: false, count: 0, reason: 'missing-context' });
    return [];
  }

  // Do not expose a previously-created summary while its saved rows are being
  // restored and enriched from the PC map layers.
  if (options.openPanel === true && pcSummaryPanel && !pcSummaryExplicitlyOpened) {
    pcSummaryPanel.style.display = 'none';
  }

  if (!options.silent) {
    setCommand('Loading Area Selections');
    setStatus('Loading current area selection results...');
  }

  try {
    let selections;
    if (isLocalPCSelectionPreview()) {
      selections = loadLocalPCSelections(ctx);
    } else {
      const response = await secureFetch(
        `${config.API_URL}spatial-search/pcdraw/load?countryId=${encodeURIComponent(ctx.countryId)}&languageId=${encodeURIComponent(ctx.languageId)}`
      );

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(data?.message || `PC draw load failed with status ${response.status}`);
      }

      selections = Array.isArray(data) ? data : [];
    }

    pcDrawSelectionsByType.clear();
    pcSummaryPage = 1;

    selections.forEach(selection => {
      const type = getRuntimePCType(getPCSelectionType(selection), ctx.countryId);
      if (!type) return;

      normalizeLoadedPCDrawSelections(selection, type).forEach(normalized => {
        upsertPCDrawSelection(type, normalized);
      });
    });

    pcDrawSelectionsByType.forEach((_, type) => deduplicatePCDrawSelections(type));
    const savedSelections = getPCDrawSelections();
    // Saved rows are the summary's source of truth. Restoring their map
    // geometry is optional enrichment and must not make the grid disappear.
    if (savedSelections.length && summaryVisibilityToken === pcSummaryDeferredOpenToken) {
      if (options.openPanel === true) pcSummaryMinimized = false;
      renderPCSummaryPanel();
    }
    await waitForPCSelectableLayers(savedSelections);
    const availableFeatures = highlightLoadedPCSelections(savedSelections, {
      fitToResults: false, emitMessages: false, silent: true
    });
    if (options.fitToResults !== false && availableFeatures.length) {
      fitPCSelectionLayerToFeatures(availableFeatures);
    }
    await loadPCSelectionFeaturesById(savedSelections);
    let highlighted = await highlightLoadedPCSelectionsWhenReady(savedSelections, {
      fitToResults: false,
      loadFullExtent: options.fitToResults !== false,
      progressiveFit: options.fitToResults !== false,
      emitMessages: false
    });

    const expectedFeatureCount = getPCSelectionExpectedFeatureCount(savedSelections);
    if (highlighted.length < expectedFeatureCount) {
      await waitForPCSelectableLayers(savedSelections, { maxAttempts: 16, delayMs: 300 });
      highlighted = await highlightLoadedPCSelectionsWhenReady(savedSelections, {
        fitToResults: false,
        loadFullExtent: true,
        progressiveFit: options.fitToResults !== false,
        maxAttempts: 16,
        emitMessages: false
      });
    }

    if (options.openPanel === true && summaryVisibilityToken === pcSummaryDeferredOpenToken) {
      pcSummaryMinimized = false;
    }
    const summaryRowsReady = arePCSummaryRowsEnriched();
    const summaryDismissed = summaryVisibilityToken !== pcSummaryDeferredOpenToken;
    if (!summaryDismissed && (pcSummaryExplicitlyOpened || savedSelections.length)) {
      renderPCSummaryPanel();
      if (pcSummaryExplicitlyOpened || (options.openPanel === true && options.showImmediately !== true)) {
        showPCSummaryPanel();
      }
    } else if (!summaryDismissed && pcSummaryPanel && options.openPanel === true) {
      pcSummaryPanel.style.display = 'none';
      setDockVisible('pc-summary', false);
    }
    if (!summaryDismissed && savedSelections.length && !summaryRowsReady) {
      schedulePCSummaryOpenWhenReady(savedSelections, {
        ...options,
        openPanel: options.openPanel === true && options.showImmediately !== true
      });
    }
    if (options.fitToResults !== false && highlighted.length) {
      scheduleFinalPCSelectionFit();
    }
    sendPCAreaSnapshot(highlighted);
    if (!options.silent) {
      setStatus(`${highlighted.length} Area Selection feature(s) highlighted`);
      setCommand('Ready');
    }
    notifyPCCurrentResultsLoaded({ success: true, count: highlighted.length });
    return highlighted;
  } catch (error) {
    console.error('PC draw load failed:', error);
    if (!options.silent) {
      setStatus('Area Selection current results failed to load');
      setCommand('Ready');
    }
    notifyPCCurrentResultsLoaded({ success: false, count: 0, error: error?.message || String(error) });
    return [];
  } finally {
    pcDrawLoadInFlight = false;
  }
}
